import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import ModalWhatsApp from "../components/ModalWhatsApp";
import ModalEmail from "../components/ModalEmail";
import ModalAccionesSeleccionPropiedades from "../components/propiedades/ModalAccionesSeleccionPropiedades";
import ModalMotivoBajaPropiedad from "../components/propiedades/ModalMotivoBajaPropiedad";
import ModalRepublicarPortales, {
  cargarItemsPublicacionesPrevias,
} from "../components/propiedades/ModalRepublicarPortales";
import ModalEnviarLocalizacion from "../components/propiedades/ModalEnviarLocalizacion";
import ModalImprimirPropiedades from "../components/propiedades/ModalImprimirPropiedades";
import ModalContactosInteresados from "../components/propiedades/ModalContactosInteresados";
import ModalActividadPropiedad from "../components/propiedades/ModalActividadPropiedad";
import ModalCrearPropietario from "../components/propiedades/ModalCrearPropietario";
import PropietarioBuscarOCrear from "../components/propiedades/PropietarioBuscarOCrear";
import { FichaResumenGaleria, FichaResumenVistaSecciones } from "../components/propiedades/FichaResumenVistaSecciones";
import { cargarDatosFichaResumen } from "../components/propiedades/cargarDatosFichaResumen";
import VistaPreviaRefPrecio from "../components/propiedades/VistaPreviaRefPrecio";
import { FICHA_RESUMEN_ROOT_CLASS, fichaResumenFontSize } from "../components/propiedades/fichaResumenTypography";
import "../components/propiedades/fichaResumenTypography.css";
import BotonesVisorUbicacion from "../components/propiedades/BotonesVisorUbicacion";
import { nombrePoblacionDisplay } from "../utils/poblacionesFavoritas";
import { resolveVideoForPresentacion, videosPlayables } from "../presentacion/presentacionVideo";
import { etiquetaEstadoPropiedad } from "../utils/propiedadCartera";
import usePermiso from "../hooks/usePermiso";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (v) => UUID_RE.test(String(v || ""));

const TODAS_COLUMNAS = [
  { id: "select", label: "", width: 40, fija: true },
  { id: "publicada", label: "Web", width: 50, fija: true },
  { id: "imagenes", label: "Imágenes", width: 160, fija: true },
  { id: "operaciones", label: "Op. activas", width: 150, fija: true },
  { id: "tipo", label: "Tipo", width: 120, fija: true },
  { id: "precio_venta", label: "Precio venta", width: 110, fija: true },
  { id: "localidad", label: "Localidad", width: 100 },
  { id: "area", label: "Zona", width: 130 },
  { id: "habitaciones", label: "Hab.", width: 70, fija: true },
  { id: "piscina", label: "Pisc.", width: 65, fija: true },
  { id: "propietario", label: "Propietario", width: 150 },
  { id: "agente", label: "Agente", width: 150 },
  { id: "direccion", label: "Dirección", width: 200 },
  { id: "exclusiva", label: "Exclusiva", width: 90 },
  { id: "exclusiva_hasta", label: "Fin exclusiva", width: 120 },
  { id: "fecha_alta", label: "Fecha de alta", width: 110, fija: true },
  // Opcionales: visibles solo si el usuario las activa en ⚙️ Columnas
  { id: "fecha_actualizacion", label: "Fecha de actualización", width: 130, visiblePorDefecto: false },
  { id: "hist_leads_total", label: "Leads", width: 56, visiblePorDefecto: false },
  { id: "hist_citas_total", label: "Visitas", width: 64, visiblePorDefecto: false },
  { id: "ano_construccion", label: "Año constr.", width: 88, visiblePorDefecto: false },
  { id: "num_aseos", label: "Aseos", width: 56, visiblePorDefecto: false },
  { id: "num_banos", label: "Baños", width: 56, visiblePorDefecto: false },
  { id: "coordenadas", label: "Coordenadas", width: 118, visiblePorDefecto: false },
  { id: "parcela", label: "Parcela", width: 72, visiblePorDefecto: false },
  { id: "m2_construidos", label: "Sup.Constr.", width: 88, visiblePorDefecto: false },
  { id: "vistas", label: "Vistas", width: 120, visiblePorDefecto: false },
  { id: "urbanizacion", label: "Urbanización", width: 130, visiblePorDefecto: false },
];

const COL_STORAGE_KEY = "vitalcasa_cols_config";

function cargarConfigColumnas() {
  try {
    const saved = localStorage.getItem(COL_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Añadir columnas nuevas que no existan en config guardada
      const ids = parsed.map(c => c.id);
      TODAS_COLUMNAS.forEach(c => {
        if (!ids.includes(c.id)) {
          parsed.push({
            id: c.id,
            visible: c.visiblePorDefecto !== false,
            width: c.width,
          });
        }
      });
      return parsed;
    }
  } catch (e) {}
  return TODAS_COLUMNAS.map(c => ({
    id: c.id,
    visible: c.visiblePorDefecto !== false,
    width: c.width,
  }));
}

function guardarConfigColumnas(config) {
  try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(config)); } catch (e) {}
}

/** Texto del input de precio: siempre euros enteros (evita "23500000.00" por NUMERIC/string). */
function precioVentaParaInput(v) {
  if (v == null || v === "") return "";
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? String(n) : "";
}

/**
 * Texto del input → euros enteros.
 * Evita que "23500000.00" con replace(/\D/g) se convierta en 2350000000.
 */
function parsePrecioVentaDesdeInput(raw) {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.round(raw);
  const s = String(raw).trim().replace(/\s/g, "").replace(/€/gi, "");
  if (s === "") return null;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  const decPos = Math.max(lastComma, lastDot);
  const afterDec = decPos >= 0 ? s.slice(decPos + 1) : "";
  if (decPos >= 0 && /^\d{1,2}$/.test(afterDec)) {
    const intOnly = s.slice(0, decPos).replace(/[^\d]/g, "");
    if (intOnly === "") return null;
    return parseInt(intOnly, 10);
  }
  const digits = s.replace(/[^\d]/g, "");
  return digits === "" ? null : parseInt(digits, 10);
}

function clampFichaResumenPos(left, top, panelEl) {
  if (typeof window === "undefined") return { left: 0, top: 0 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pw = panelEl?.offsetWidth ?? Math.min(720, vw - 8);
  const margin = 48;
  return {
    left: Math.min(Math.max(left, -pw + margin), vw - margin),
    top: 0,
  };
}

function FichaResumenModal({ propiedad: p, datos: d, onClose, theme }) {
  const t = theme.colors;
  const fotos = d.fotos || [];
  const panelRef = useRef(null);
  const dragCleanupRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const el = panelRef.current;
    const w = el?.offsetWidth ?? Math.min(720, window.innerWidth - 8);
    setPos(clampFichaResumenPos(window.innerWidth - w, 0, el));
  }, [p.id]);

  useEffect(
    () => () => {
      if (dragCleanupRef.current) {
        dragCleanupRef.current();
        dragCleanupRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const onResize = () => {
      setPos((prev) => clampFichaResumenPos(prev.left, prev.top, panelRef.current));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onBarraPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.target.closest("button")) return;
    if (dragCleanupRef.current) dragCleanupRef.current();
    const dr = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origLeft: pos.left,
      origTop: pos.top,
    };

    const onMove = (ev) => {
      if (ev.pointerId !== dr.pointerId) return;
      const el = panelRef.current;
      setPos(
        clampFichaResumenPos(
          dr.origLeft + ev.clientX - dr.startX,
          dr.origTop + ev.clientY - dr.startY,
          el
        )
      );
    };
    const end = (ev) => {
      if (ev.pointerId !== dr.pointerId) return;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
      dragCleanupRef.current = null;
    };
    dragCleanupRef.current = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", end);
    document.addEventListener("pointercancel", end);
  };

  return (
    <div className="fixed inset-0 z-50" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div
        ref={panelRef}
        className={`shadow-2xl overflow-y-auto flex flex-col ${FICHA_RESUMEN_ROOT_CLASS}`}
        style={{
          position: "fixed",
          left: pos.left,
          top: 0,
          height: "100vh",
          width: "min(720px, calc(100vw - 8px))",
          background: t.bgCard,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          role="toolbar"
          aria-label="Mover vista previa"
          className="flex items-center justify-between px-2.5 py-1 sticky top-0 z-10 select-none shrink-0"
          style={{
            background: t.bgCard,
            borderBottom: `1px solid ${t.border}`,
            cursor: "grab",
            touchAction: "none",
            minHeight: 28,
          }}
          onPointerDown={onBarraPointerDown}
          title="Arrastra para mover el panel"
        >
          <VistaPreviaRefPrecio prop={p} fontSize={fichaResumenFontSize(11)} className="flex-1 min-w-0 pr-2" />
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 leading-none"
            style={{ color: t.textMuted, cursor: "pointer", fontSize: fichaResumenFontSize(16), padding: "0 2px" }}
          >
            ✕
          </button>
        </div>

        <div className="px-3 pt-3 flex-1 min-h-0 overflow-y-auto">
          <FichaResumenGaleria fotos={fotos} videos={d.videos || []} theme={theme} />
          <FichaResumenVistaSecciones p={p} d={d} theme={theme} />
        </div>
      </div>
    </div>
  );
}

const FILTROS_CARTERA = [
  {
    id: "activas",
    label: "Activas",
    hint: "En cartera operativa de venta: disponibles, publicadas en web y sin operación de alquiler.",
  },
  {
    id: "alquiler",
    label: "Alquiler",
    hint: "Disponibles en cartera con operación Alquiler o Alquiler vacacional.",
  },
  {
    id: "ocultas",
    label: "Ocultas",
    hint: "Marcadas como frías en el embudo; retiradas de la vista habitual.",
  },
  {
    id: "no_disponibles",
    label: "No disponibles",
    hint: "Fuera de mercado temporalmente hasta la fecha indicada; vuelven a Activas automáticamente.",
  },
  {
    id: "reservadas",
    label: "Reservadas",
    hint: "No disponibles para nuevas operaciones; pueden seguir visibles en web según los datos.",
  },
  {
    id: "vendidas",
    label: "Vendidas/Alquiladas",
    hint: "Operación cerrada (venta o alquiler): no disponibles y dadas de baja en publicación web.",
  },
  {
    id: "eliminadas",
    label: "Eliminadas",
    hint: "Propiedades archivadas. Puedes restaurarlas a Activas o eliminarlas definitivamente (irreversible).",
  },
];

const PRESETS_POCA_ACTIVIDAD = [
  { id: "1m", label: "Último mes", months: 1 },
  { id: "3m", label: "Últimos 3 meses", months: 3 },
  { id: "6m", label: "Últimos 6 meses", months: 6 },
];

function fechaYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fechaHaceMeses(months) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() - months);
  return fechaYmd(d);
}

export default function Propiedades() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnToListado = `${location.pathname}${location.search}`;
  const { puede } = usePermiso();
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [mensaje, setMensaje] = useState("");
  const [propiedades, setPropiedades] = useState([]);
  const [totalResultados, setTotalResultados] = useState(0);
  /** Cartera a la que corresponden `propiedades` y `totalResultados` (evita mostrar totales viejos al cambiar de pestaña). */
  const [metaCartera, setMetaCartera] = useState("activas");
  const [totalPages, setTotalPages] = useState(1);
  const [pageActual, setPageActual] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const debounceRef = useRef(null);
  const [carteraFiltro, setCarteraFiltro] = useState("activas");
  const carteraRef = useRef("activas");
  carteraRef.current = carteraFiltro;
  // Cada pestaña de cartera tiene su propio permiso (prop_cartera_<id>): un rol/usuario
  // puede tener, p. ej., Activas y Vendidas pero no Ocultas.
  const filtrosCarteraVisibles = useMemo(
    () => FILTROS_CARTERA.filter((f) => puede(`prop_cartera_${f.id}`, "ver")),
    [puede]
  );
  /** Id incremental de la última petición `cargar()` en curso: descarta respuestas obsoletas
   * (p.ej. al cambiar rápido de pestaña) para que no pisen datos más recientes con 0 o con
   * resultados de otra cartera. */
  const requestIdRef = useRef(0);

  const aplicarHintReferenciaBusqueda = (textoBuscar, total, carteraActual, hint, sinVisitasActivo) => {
    const t = (textoBuscar || "").trim();
    if (!t || !hint?.encontrada) {
      setModalRefOtraCartera(null);
      return;
    }
    // Coincidencia múltiple (p.ej. "10005" encuentra 10005-A/L/T/...): la
    // pestaña actual puede tener alguna de esas coincidencias aunque no todas
    // estén repartidas igual, así que se comprueba contra `porCartera`.
    if (hint.multiple) {
      const enVista = hint.porCartera?.some((x) => x.cartera === carteraActual);
      if (!enVista) {
        setModalRefOtraCartera({ ...hint, filtradaEnVista: false, textoBuscado: t });
        return;
      }
      if (total === 0) {
        setModalRefOtraCartera({ ...hint, filtradaEnVista: true, sinVisitasActivo: !!sinVisitasActivo, textoBuscado: t });
        return;
      }
      setModalRefOtraCartera(null);
      return;
    }
    if (hint.cartera !== carteraActual) {
      setModalRefOtraCartera({ ...hint, filtradaEnVista: false });
      return;
    }
    if (total === 0) {
      setModalRefOtraCartera({
        ...hint,
        filtradaEnVista: true,
        sinVisitasActivo: !!sinVisitasActivo,
      });
      return;
    }
    setModalRefOtraCartera(null);
  };

  const verificarReferenciaOtraCartera = async (textoBuscar, total, carteraActual, sinVisitasActivo) => {
    const t = (textoBuscar || "").trim();
    if (!t) {
      setModalRefOtraCartera(null);
      return;
    }
    try {
      const res = await api.get(`/propiedades/ubicacion-busqueda?q=${encodeURIComponent(t)}`);
      aplicarHintReferenciaBusqueda(textoBuscar, total, carteraActual, res.data, sinVisitasActivo);
    } catch {
      setModalRefOtraCartera(null);
    }
  };

  const buildQueryPropiedades = (pagina, textoBuscar, cartera, sinVisitasDesde, ordenMenosActividad, soloExclusivas) => {
    const q = new URLSearchParams();
    q.set("cartera", cartera);
    if (textoBuscar) q.set("buscar", textoBuscar);
    else q.set("page", String(pagina));
    if (sinVisitasDesde) q.set("sin_visitas_desde", sinVisitasDesde);
    if (ordenMenosActividad) q.set("orden_menos_actividad", "1");
    if (soloExclusivas) q.set("exclusivas", "1");
    return q;
  };

  const cargar = async (
    pagina = 1,
    textoBuscar = "",
    overrideCartera,
    overrideSinVisitas,
    overrideOrdenMenos,
    overrideExclusivas
  ) => {
    const cartera = overrideCartera !== undefined ? overrideCartera : carteraFiltro;
    const sinVisitas =
      overrideSinVisitas !== undefined ? overrideSinVisitas : pocaActividadRef.current;
    const ordenMenos =
      overrideOrdenMenos !== undefined ? overrideOrdenMenos : pocaActividadOrdenRef.current;
    const soloExclusivas =
      overrideExclusivas !== undefined ? overrideExclusivas : exclusivasFiltroRef.current;
    const myRequestId = ++requestIdRef.current;
    setCargando(true);
    try {
      const q = buildQueryPropiedades(pagina, textoBuscar, cartera, sinVisitas, ordenMenos, soloExclusivas);
      const res = await api.get(`/propiedades?${q.toString()}`);
      if (requestIdRef.current !== myRequestId) return; // respuesta obsoleta: ya se lanzó otra petición más reciente
      const rows = res.data?.data;
      const total = Number(res.data?.total) || 0;
      setPropiedades(Array.isArray(rows) ? rows : []);
      setTotalResultados(total);
      setMetaCartera(cartera);
      setTotalPages(res.data?.totalPages || 1);
      setPageActual(pagina);
      if (textoBuscar.trim()) {
        const hint = res.data?.referencia_en_cartera;
        if (hint?.encontrada) {
          aplicarHintReferenciaBusqueda(textoBuscar, total, cartera, hint, !!sinVisitas);
        } else if (total === 0) {
          await verificarReferenciaOtraCartera(textoBuscar, total, cartera, !!sinVisitas);
        } else {
          setModalRefOtraCartera(null);
        }
      } else {
        setModalRefOtraCartera(null);
      }
    } catch (err) {
      if (requestIdRef.current !== myRequestId) return; // obsoleta: no pisar datos de una petición posterior
      console.error("Error cargando propiedades:", err);
      const st = err.response?.status;
      const msg = err.response?.data?.error || err.message || "Error al cargar propiedades";
      if (st === 503 && /arrancando|boot/i.test(String(err.response?.data?.mensaje || msg))) {
        setMensaje("El servidor está arrancando. Reintenta en unos segundos.");
      } else {
        setMensaje(`No se pudo cargar el listado: ${msg}`);
      }
      setPropiedades([]);
      setTotalResultados(0);
    } finally {
      if (requestIdRef.current === myRequestId) setCargando(false);
    }
  };

  const [seleccionadas, setSeleccionadas] = useState([]);
  const [colConfig, setColConfig] = useState(cargarConfigColumnas());
  const [mostrarColConfig, setMostrarColConfig] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [visor, setVisor] = useState({ abierto: false, fotos: [], indice: 0 });
  const [visorVideo, setVisorVideo] = useState({
    abierto: false,
    cargando: false,
    error: "",
    videos: [],
    indice: 0,
    referencia: "",
  });
  const [fichaResumen, setFichaResumen] = useState({ abierta: false, propiedad: null, datos: null });
  const [agentes, setAgentes] = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [poblaciones, setPoblaciones] = useState([]);
  const [editandoAgenteId, setEditandoAgenteId] = useState(null);
  const [editandoCampo, setEditandoCampo] = useState(null); // { propId, campo }
  const puedeEditarInline = puede("prop_list_edicion_inline", "ver") && puede("prop_list_edicion_inline", "modificar");
  const [pocaActividadPanel, setPocaActividadPanel] = useState(false);
  const [pocaActividadDesde, setPocaActividadDesde] = useState(null);
  const [pocaActividadEtiqueta, setPocaActividadEtiqueta] = useState("");
  const [pocaActividadFechaCustom, setPocaActividadFechaCustom] = useState("");
  const [pocaActividadOrdenMenos, setPocaActividadOrdenMenos] = useState(false);
  const pocaActividadRef = useRef(null);
  const pocaActividadOrdenRef = useRef(false);
  pocaActividadRef.current = pocaActividadDesde;
  pocaActividadOrdenRef.current = pocaActividadOrdenMenos;
  const [exclusivasFiltro, setExclusivasFiltro] = useState(false);
  const exclusivasFiltroRef = useRef(false);
  exclusivasFiltroRef.current = exclusivasFiltro;
  const tablaScrollRef = useRef(null);
  const tablaHeaderScrollRef = useRef(null);
  const tablaHeaderInnerRef = useRef(null);
  const tablaScrollFooterRef = useRef(null);
  const tablaScrollFooterInnerRef = useRef(null);
  const tablaScrollSyncRef = useRef(false);
  const headerStickyRef = useRef(null);
  const colConfigBtnRef = useRef(null);
  const colConfigPanelRef = useRef(null);
  const [colMenuPos, setColMenuPos] = useState(null);
  const [tablaTieneOverflowX, setTablaTieneOverflowX] = useState(false);
  const [headerStickyBottom, setHeaderStickyBottom] = useState(108);

  useLayoutEffect(() => {
    if (!mostrarColConfig || !colConfigBtnRef.current) {
      setColMenuPos(null);
      return;
    }
    const updatePos = () => {
      const btn = colConfigBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const panelW = 288;
      const panelMaxH = Math.min(window.innerHeight * 0.7, 420);
      const gap = 6;
      const spaceBelow = window.innerHeight - r.bottom - gap;
      const spaceAbove = r.top - gap;
      const openBelow = spaceBelow >= 180 || spaceBelow >= spaceAbove;
      const top = openBelow
        ? r.bottom + gap
        : Math.max(gap, r.top - gap - panelMaxH);
      let left = r.right - panelW;
      left = Math.max(gap, Math.min(left, window.innerWidth - panelW - gap));
      setColMenuPos({ top, left, width: panelW, maxHeight: panelMaxH });
    };
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [mostrarColConfig]);

  useLayoutEffect(() => {
    const el = headerStickyRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setHeaderStickyBottom(Math.max(54, Math.round(rect.bottom)));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [colConfig.length, seleccionadas.length, pocaActividadEtiqueta, pocaActividadOrdenMenos, busqueda, carteraFiltro]);

  useEffect(() => {
    if (!mostrarColConfig) return;
    const onPointerDown = (e) => {
      const inBtn = colConfigBtnRef.current?.contains(e.target);
      const inPanel = colConfigPanelRef.current?.contains(e.target);
      if (!inBtn && !inPanel) setMostrarColConfig(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [mostrarColConfig]);

  const [modalAcciones, setModalAcciones] = useState(false);
  const [modalWA, setModalWA] = useState(false);
  const [modalMail, setModalMail] = useState(false);
  const [modalLoc, setModalLoc] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [modalInteresados, setModalInteresados] = useState(false);
  const [modalCruceClientes, setModalCruceClientes] = useState(false);
  const [modalActividad, setModalActividad] = useState({ abierto: false, propiedad: null });
  const [modalPropietarioTabla, setModalPropietarioTabla] = useState(null);
  const [modalRefOtraCartera, setModalRefOtraCartera] = useState(null);
  const [modalMotivoBaja, setModalMotivoBaja] = useState(null);
  const [modalPortalesPrevios, setModalPortalesPrevios] = useState(null);
  const [contactoInteresadoWA, setContactoInteresadoWA] = useState(null);
  const [contactoInteresadoEmail, setContactoInteresadoEmail] = useState(null);
  const prevSelCount = useRef(0);

  const abrirBuscadorPropietarioTabla = (p) => {
    setEditandoCampo({ propId: p.id, campo: "propietario" });
  };

  const abrirModalPropietarioTabla = (p) => {
    const label = (p.propietario || "").trim();
    const parts = label.split(/\s+/).filter(Boolean);
    const contactoId = isUuid(p.propietario_contacto_id) ? p.propietario_contacto_id : undefined;
    if (!contactoId && !label) {
      abrirBuscadorPropietarioTabla(p);
      return;
    }
    setModalPropietarioTabla({
      propiedadId: p.id,
      propietarioEditar: {
        contacto_id: contactoId,
        nombre: parts[0] || label,
        apellidos: parts.slice(1).join(" "),
        porcentaje: 100,
      },
      textoBusqueda: "",
    });
  };

  const vincularPropietarioEnTabla = async (propiedadId, contactoId, porcentaje = 100) => {
    const res = await api.patch(`/propiedades/${propiedadId}/propietario-principal`, {
      contacto_id: contactoId,
      porcentaje,
    });
    return res.data;
  };

  const mapPropiedadesParaEnvio = (lista) =>
    lista.map((p) => {
      const pobDisplay = nombrePoblacionDisplay(p, poblaciones);
      return {
      id: p.id,
      titulo: p.titulo || "Sin título",
      detalle: `${p.tipo || ""} · ${pobDisplay}`,
      precio: p.precio_venta ? `${Number(p.precio_venta).toLocaleString("es-ES")} €` : "Consultar",
      icono: "🏠",
      tipo: p.tipo || "",
      poblacion: pobDisplay,
      zona: p.zona || "",
      habitaciones: p.num_dormitorios ?? null,
      banos: null,
      m2: null,
      referencia: p.referencia || "",
      descripcion: "",
    };
    });

  const abrirFichaResumen = async (p) => {
    try {
      const datos = await cargarDatosFichaResumen(p.id);
      setFichaResumen({
        abierta: true,
        propiedad: p,
        datos,
      });
    } catch (err) {
      console.error("Error cargando ficha resumen:", err);
    }
  };
  
  const abrirVisor = (fotos) => {
    setVisor({ abierto: true, fotos, indice: 0 });
  };

  const cerrarVisor = () => {
    setVisor({ abierto: false, fotos: [], indice: 0 });
  };

  const visorAnterior = () => {
    setVisor(v => ({ ...v, indice: v.indice > 0 ? v.indice - 1 : v.fotos.length - 1 }));
  };

  const visorSiguiente = () => {
    setVisor(v => ({ ...v, indice: v.indice < v.fotos.length - 1 ? v.indice + 1 : 0 }));
  };

  const cerrarVisorVideo = useCallback(() => {
    setVisorVideo({
      abierto: false,
      cargando: false,
      error: "",
      videos: [],
      indice: 0,
      referencia: "",
    });
  }, []);

  const abrirVisorVideos = useCallback(async (p, e) => {
    e?.stopPropagation?.();
    setVisorVideo({
      abierto: true,
      cargando: true,
      error: "",
      videos: [],
      indice: 0,
      referencia: p.referencia || "",
    });
    try {
      const r = await api.get(`/propiedades/${p.id}/videos`);
      const rows = r.data?.data || [];
      const playables = videosPlayables(rows);
      if (playables.length === 0) {
        setVisorVideo((prev) => ({
          ...prev,
          cargando: false,
          error: "No hay vídeos reproducibles",
          videos: [],
        }));
        return;
      }
      setVisorVideo({
        abierto: true,
        cargando: false,
        error: "",
        videos: playables,
        indice: 0,
        referencia: p.referencia || "",
      });
    } catch (err) {
      setVisorVideo((prev) => ({
        ...prev,
        cargando: false,
        error: err.response?.data?.error || err.message || "Error al cargar vídeos",
        videos: [],
      }));
    }
  }, []);

  const visorVideoAnterior = useCallback(() => {
    setVisorVideo((v) => {
      if (!v.videos.length) return v;
      return {
        ...v,
        indice: v.indice > 0 ? v.indice - 1 : v.videos.length - 1,
      };
    });
  }, []);

  const visorVideoSiguiente = useCallback(() => {
    setVisorVideo((v) => {
      if (!v.videos.length) return v;
      return {
        ...v,
        indice: v.indice < v.videos.length - 1 ? v.indice + 1 : 0,
      };
    });
  }, []);

  useEffect(() => {
    if (!visorVideo.abierto || visorVideo.cargando || visorVideo.videos.length === 0) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        visorVideoAnterior();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        visorVideoSiguiente();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        cerrarVisorVideo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    visorVideo.abierto,
    visorVideo.cargando,
    visorVideo.videos.length,
    visorVideoAnterior,
    visorVideoSiguiente,
    cerrarVisorVideo,
  ]);

  const startX = useRef(0);
  const startW = useRef(0);
  const [dragCol, setDragCol] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  useReloadOnRouteEnter(() => {
    const texto = busqueda;
    const cartera = carteraFiltro;
    const sinVisitas = pocaActividadRef.current;
    const ordenMenos = pocaActividadOrdenRef.current;
    cargar(1, texto, cartera, sinVisitas, ordenMenos).then(() => {
      const scrollY = sessionStorage.getItem("propiedades_scrollY");
      if (scrollY) {
        setTimeout(() => window.scrollTo({ top: parseInt(scrollY, 10), behavior: "instant" }), 50);
        sessionStorage.removeItem("propiedades_scrollY");
      }
    });
  });

  useEffect(() => {
    api.get("/agentes").then(res => {
      const data = res.data?.data || res.data || [];
      setAgentes(Array.isArray(data) ? data : []);
    }).catch(() => {});
    api.get("/auxiliares/tipo-inmueble").then(res => setTiposInmueble(res.data?.data || [])).catch(() => {});
    api.get("/auxiliares/zona").then(res => setZonas(res.data?.data || [])).catch(() => {});
    api.get("/auxiliares/poblaciones-todas").then(res => {
      const arr = res.data?.data || [];
      arr.sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", { sensitivity: "base" }));
      setPoblaciones(arr);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const msg = sessionStorage.getItem("vitalcasa_msg");
    if (msg) {
      setMensaje(msg);
      sessionStorage.removeItem("vitalcasa_msg");
      setTimeout(() => setMensaje(""), 4000);
    }
  }, []);

  useEffect(() => {
    const previewId = searchParams.get("preview");
    if (!previewId) return;
    api.get("/propiedades/" + previewId).then(res => {
      const p = res.data.data || res.data;
      if (p?.id) abrirFichaResumen(p);
    }).catch(() => {});
  }, [searchParams]);

  const onCambioBusqueda = (valor) => {
    setBusqueda(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!valor.trim()) setModalRefOtraCartera(null);
    debounceRef.current = setTimeout(() => cargar(1, valor), 350);
  };

  const cambiarCartera = (id) => {
    setCarteraFiltro(id);
    cargar(1, busqueda, id);
  };

  /** Va a la pestaña de una coincidencia concreta del modal de referencias, buscando solo esa referencia. */
  const verReferenciaEnCartera = (referencia, cartera) => {
    setModalRefOtraCartera(null);
    setBusqueda(referencia);
    setCarteraFiltro(cartera);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    cargar(1, referencia, cartera);
  };

  // Si la pestaña activa (por defecto "activas") no está permitida para este
  // rol/usuario, saltar a la primera pestaña de cartera que sí lo esté.
  useEffect(() => {
    if (filtrosCarteraVisibles.length === 0) return;
    if (filtrosCarteraVisibles.some((f) => f.id === carteraFiltro)) return;
    cambiarCartera(filtrosCarteraVisibles[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosCarteraVisibles]);

  const aplicarPocaActividad = (desde, etiqueta, ordenMenos = pocaActividadOrdenMenos) => {
    setPocaActividadDesde(desde);
    setPocaActividadEtiqueta(etiqueta);
    setPocaActividadFechaCustom("");
    cargar(1, busqueda, undefined, desde, ordenMenos);
  };

  const quitarPocaActividad = () => {
    setPocaActividadDesde(null);
    setPocaActividadEtiqueta("");
    setPocaActividadPanel(false);
    setPocaActividadFechaCustom("");
    setPocaActividadOrdenMenos(false);
    cargar(1, busqueda, undefined, null, false);
  };

  const toggleOrdenMenosActividad = (checked) => {
    setPocaActividadOrdenMenos(checked);
    const desde = pocaActividadRef.current;
    if (desde) {
      cargar(1, busqueda, undefined, desde, checked);
    }
  };

  const toggleFiltroExclusivas = () => {
    const next = !exclusivasFiltroRef.current;
    setExclusivasFiltro(next);
    cargar(1, busqueda, undefined, undefined, undefined, next);
  };

  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const SORT_VALUE = {
    operaciones: p => p.referencia || "",
    tipo:        p => (p.tipo || "").toLowerCase(),
    precio_venta:p => Number(p.precio_venta) || 0,
    localidad:   p => nombrePoblacionDisplay(p, poblaciones).toLowerCase(),
    area:        p => (p.zona || "").toLowerCase(),
    agente:      p => (p.agente_nombre || "").toLowerCase(),
    exclusiva:   p => (p.exclusiva ? 1 : 0),
    exclusiva_hasta: p => p.exclusiva_hasta ? new Date(p.exclusiva_hasta).getTime() : 0,
    fecha_alta:  p => p.fecha_alta ? new Date(p.fecha_alta).getTime() : 0,
    hist_leads_total: p => Number(p.hist_leads_total) || 0,
    hist_citas_total: p => Number(p.hist_citas_total) || 0,
    ano_construccion: p => Number(p.ano_construccion) || 0,
    num_aseos: p => Number(p.num_aseos) || 0,
    num_banos: p => Number(p.num_banos) || 0,
    m2_construidos: p => Number(p.m2_construidos) || 0,
    parcela: p => String(p.parcela || "").toLowerCase(),
    coordenadas: p => {
      const lat = p.latitud != null && p.latitud !== "" ? Number(p.latitud) : null;
      const lng = p.longitud != null && p.longitud !== "" ? Number(p.longitud) : null;
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return -1;
      return lat * 1e6 + lng;
    },
    vistas: p => String(p.vistas || "").toLowerCase(),
    urbanizacion: p => String(p.urbanizacion || "").toLowerCase(),
  };

  const toggleSort = (colId) => {
    if (sortCol === colId) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(colId); setSortDir("asc"); }
  };

  const filtradas = sortCol && SORT_VALUE[sortCol]
    ? [...propiedades].sort((a, b) => {
        const va = SORT_VALUE[sortCol](a);
        const vb = SORT_VALUE[sortCol](b);
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ?  1 : -1;
        return 0;
      })
    : propiedades;

  const propsSeleccionadas = filtradas.filter((p) => seleccionadas.includes(p.id));

  useEffect(() => {
    const n = seleccionadas.length;
    if (n > 0 && prevSelCount.current === 0) setModalAcciones(true);
    prevSelCount.current = n;
  }, [seleccionadas.length]);

  const aplicarMasivo = async (patch, opts = {}) => {
    // Snapshot: no depender del state React durante awaits largos
    const ids = [...seleccionadas];
    const fallos = [];
    setSeleccionadas([]);
    const resultados = await Promise.allSettled(
      ids.map((id) => api.put(`/propiedades/${id}`, patch).then(() => id)),
    );
    for (let i = 0; i < resultados.length; i++) {
      const r = resultados[i];
      if (r.status === "fulfilled") continue;
      const id = ids[i];
      const e = r.reason;
      console.error("Error actualizando propiedad", id, e);
      const ref = propiedades.find((p) => p.id === id)?.referencia || id;
      fallos.push({ ref, error: e?.response?.data?.error || e?.message || "Error desconocido" });
    }
    if (fallos.length === 0) {
      setMensaje(`Cambios aplicados a ${ids.length} propiedad(es)`);
      setTimeout(() => setMensaje(""), 4000);
    } else {
      const okCount = ids.length - fallos.length;
      const detalle = fallos.map((f) => `${f.ref}: ${f.error}`).join("\n");
      await dialogs.alert(
        `${okCount > 0 ? `Se aplicó a ${okCount} propiedad(es), pero ` : "No se pudo aplicar: "}` +
        `${fallos.length} fallaron:\n${detalle}`
      );
    }
    if (opts.carteraDespues) {
      setCarteraFiltro(opts.carteraDespues);
      await cargar(1, busqueda, opts.carteraDespues);
    } else {
      await cargar(pageActual, busqueda);
    }
  };

  const ACCIONES_CON_MOTIVO = new Set(["vendida", "reservada", "ocultar", "eliminar"]);

  const ejecutarBajaConMotivo = async (accionId, motivo) => {
    setModalMotivoBaja(null);
    const motivoTrim = String(motivo || "").trim();
    if (!motivoTrim) return;

    switch (accionId) {
      case "vendida":
        await aplicarMasivo(
          {
            disponible: false,
            publicar_web: false,
            disponible_desde: null,
            // Salir de «ocultas» (etapa frio); si no, el listado Ocultas las sigue mostrando
            etapa_pipeline: null,
            motivo_no_disponible: motivoTrim,
          },
          { carteraDespues: "vendidas" }
        );
        return;
      case "reservada":
        await aplicarMasivo({
          disponible: false,
          disponible_desde: null,
          etapa_pipeline: null,
          motivo_no_disponible: motivoTrim,
        });
        return;
      case "ocultar":
        // Sitio de fichas ya con ref (captación terminada). Frías del Pipeline
        // captaciones es solo para captaciones NO terminadas.
        await aplicarMasivo({
          etapa_pipeline: "frio",
          publicar_web: false,
          disponible: false,
          motivo_no_disponible: motivoTrim,
        });
        return;
      case "eliminar": {
        const idsEliminar = [...seleccionadas];
        setSeleccionadas([]);
        const fallosElim = [];
        const resultadosElim = await Promise.allSettled(
          idsEliminar.map((id) =>
            api.delete(`/propiedades/${id}`, { data: { motivo_no_disponible: motivoTrim } }).then(() => id),
          ),
        );
        for (let i = 0; i < resultadosElim.length; i++) {
          const r = resultadosElim[i];
          if (r.status === "fulfilled") continue;
          const id = idsEliminar[i];
          const e = r.reason;
          console.error("Error archivar propiedad", id, e);
          const ref = propiedades.find((p) => p.id === id)?.referencia || id;
          fallosElim.push({ ref, error: e?.response?.data?.error || e?.message || "Error desconocido" });
        }
        const okElim = idsEliminar.length - fallosElim.length;
        if (fallosElim.length === 0) {
          setMensaje(`${okElim} propiedad(es) archivadas (filtro Eliminadas)`);
          setTimeout(() => setMensaje(""), 4000);
        } else {
          await dialogs.alert(
            `${okElim > 0 ? `Se archivaron ${okElim}, pero ` : "No se pudo archivar: "}` +
            `${fallosElim.length} fallaron:\n` +
            fallosElim.map((f) => `${f.ref}: ${f.error}`).join("\n"),
          );
        }
        await cargar(pageActual, busqueda);
        return;
      }
      default:
        return;
    }
  };

  const onAccionSeleccion = async (accionId) => {
    setModalAcciones(false);
    if (seleccionadas.length === 0) return;

    if (ACCIONES_CON_MOTIVO.has(accionId)) {
      setModalMotivoBaja({ accionId });
      return;
    }

    switch (accionId) {
      case "whatsapp":
        setModalWA(true);
        return;
      case "email":
        setModalMail(true);
        return;
      case "localizacion":
        setModalLoc(true);
        return;
      case "imprimir":
        setModalPrint(true);
        return;
      case "hoja_visitas": {
        const w = window.open("", "_blank");
        if (w) {
          w.document.write(
            `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Hoja de visitas</title></head><body style="font-family:sans-serif;padding:24px"><h1 style="font-size:18px">Hoja de visitas</h1><p style="color:#64748b">Plantilla y campos en definición. Propiedades: ${seleccionadas.length}.</p><p style="font-size:12px">${propsSeleccionadas.map((p) => (p.referencia || p.id) + " — " + (p.titulo || "")).join("<br/>")}</p></body></html>`
          );
          w.document.close();
          w.print();
        }
        return;
      }
      case "interesados":
        if (seleccionadas.length !== 1) {
          await dialogs.alert("Selecciona una sola propiedad para ver contactos interesados.");
          setModalAcciones(true);
          return;
        }
        setModalInteresados(true);
        return;
      case "eliminar_definitiva": {
        if (carteraFiltro !== "eliminadas") {
          await dialogs.alert('Selecciona el filtro «Eliminadas», marca las propiedades y vuelve a abrir Acciones.');
          setModalAcciones(true);
          return;
        }
        const n = seleccionadas.length;
        const refs = propsSeleccionadas
          .map((p) => p.referencia || p.id)
          .slice(0, 5)
          .join(", ");
        const extra = n > 5 ? ` (+${n - 5} más)` : "";
        const ok = await dialogs.confirm(
          `¿Eliminar definitivamente ${n} propiedad(es)?\n\n${refs}${extra}\n\nSe borrarán la ficha, fotos, vídeos, documentos, historial y demás datos vinculados. Esta acción no se puede deshacer.`
        );
        if (!ok) return;
        const idsDef = [...seleccionadas];
        setSeleccionadas([]);
        const resDef = await Promise.allSettled(
          idsDef.map((id) => api.delete(`/propiedades/${id}/definitiva`)),
        );
        const errores = resDef.filter((r) => r.status === "rejected").length;
        setMensaje(
          errores > 0
            ? `Eliminadas ${n - errores} propiedad(es); ${errores} fallaron`
            : `${n} propiedad(es) eliminadas definitivamente`
        );
        setTimeout(() => setMensaje(""), 5000);
        await cargar(pageActual, busqueda);
        return;
      }
      case "restaurar_activas": {
        if (carteraFiltro !== "eliminadas") {
          await dialogs.alert('Selecciona el filtro «Eliminadas», marca las propiedades y vuelve a abrir Acciones, o usa «Restaurar a activas» desde ahí.');
          setModalAcciones(true);
          return;
        }
        if (!(await dialogs.confirm(`¿Restaurar ${seleccionadas.length} propiedad(es) a estado activo en cartera (disponibles y publicadas en web)?`))) return;
        const idsRestaurar = [...seleccionadas];
        for (const id of idsRestaurar) {
          try {
            await api.put(`/propiedades/${id}`, {
              etapa_pipeline: null,
              disponible: true,
              publicar_web: true,
            });
          } catch (e) {
            console.error(e);
          }
        }
        setSeleccionadas([]);
        setMensaje("Propiedades restauradas a activas");
        setTimeout(() => setMensaje(""), 4000);
        await cargar(pageActual, busqueda);
        try {
          const items = await cargarItemsPublicacionesPrevias(idsRestaurar);
          if (items.length) setModalPortalesPrevios(items);
        } catch (e) {
          console.warn("[portales previos]", e.message);
        }
        return;
      }
      case "devolver_activa": {
        if (!(await dialogs.confirm(`¿Devolver ${seleccionadas.length} propiedad(es) a activa? Quedarán disponibles y publicadas en web.`))) return;
        const idsDevolver = [...seleccionadas];
        for (const id of idsDevolver) {
          try {
            await api.put(`/propiedades/${id}`, {
              etapa_pipeline: null,
              disponible: true,
              publicar_web: true,
            });
          } catch (e) {
            console.error(e);
          }
        }
        setSeleccionadas([]);
        setMensaje("Propiedades devueltas a activas");
        setTimeout(() => setMensaje(""), 4000);
        await cargar(pageActual, busqueda);
        try {
          const items = await cargarItemsPublicacionesPrevias(idsDevolver);
          if (items.length) setModalPortalesPrevios(items);
        } catch (e) {
          console.warn("[portales previos]", e.message);
        }
        return;
      }
      default:
        return;
    }
  };

  const toggleSeleccion = (id) => {
    setSeleccionadas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleTodas = () => {
    if (seleccionadas.length === filtradas.length) setSeleccionadas([]);
    else setSeleccionadas(filtradas.map(p => p.id));
  };

  const toggleColumna = (colId) => {
    const nueva = colConfig.map(c => c.id === colId ? { ...c, visible: !c.visible } : c);
    setColConfig(nueva);
    guardarConfigColumnas(nueva);
  };

  const onDragStartCol = (e, colId) => {
    setDragCol(colId);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOverCol = (e, colId) => {
    e.preventDefault();
    if (colId !== dragCol) setDragOver(colId);
  };

  const onDropCol = (colId) => {
    if (!dragCol || dragCol === colId) { setDragCol(null); setDragOver(null); return; }
    const nueva = [...colConfig];
    const fromIdx = nueva.findIndex(c => c.id === dragCol);
    const toIdx = nueva.findIndex(c => c.id === colId);
    const [moved] = nueva.splice(fromIdx, 1);
    nueva.splice(toIdx, 0, moved);
    setColConfig(nueva);
    guardarConfigColumnas(nueva);
    setDragCol(null);
    setDragOver(null);
  };

  const onMouseDown = (colId, e) => {
    e.preventDefault();
    startX.current = e.clientX;
    const col = colConfig.find(c => c.id === colId);
    startW.current = col?.width || 100;
    setResizing(colId);
  };

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const diff = e.clientX - startX.current;
      const newW = Math.max(40, startW.current + diff);
      const nueva = colConfig.map(c => c.id === resizing ? { ...c, width: newW } : c);
      setColConfig(nueva);
    };
    const onUp = () => {
      setResizing(null);
      guardarConfigColumnas(colConfig);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, [resizing, colConfig]);

// La columna "select" es un control de UI (checkbox de selección), no un dato de la
// propiedad: se mantiene siempre visible aunque el resto de columnas se gobiernen por
// el nodo de permisos "prop_col_<id>" (Propiedades → Listado → ⚙️ Columnas).
const columnasVisibles = colConfig
    .filter(cc => cc.visible !== false)
    .map(cc => TODAS_COLUMNAS.find(c => c.id === cc.id))
    .filter(Boolean)
    .filter(c => c.id === "select" || puede(`prop_col_${c.id}`, "ver"));

  const columnasTabla = useMemo(() => {
    if (!pocaActividadDesde || !puede("prop_col_actividad", "ver")) return columnasVisibles;
    const actCol = { id: "actividad", label: "Actividad", width: 220 };
    if (columnasVisibles.some((c) => c.id === "actividad")) return columnasVisibles;
    const idx = columnasVisibles.findIndex((c) => c.id === "operaciones");
    const next = [...columnasVisibles];
    next.splice(idx >= 0 ? idx + 1 : 1, 0, actCol);
    return next;
  }, [columnasVisibles, pocaActividadDesde, puede]);

  const getWidth = (colId) => {
    if (colId === "actividad") return 220;
    const conf = colConfig.find(c => c.id === colId);
    const colDef = TODAS_COLUMNAS.find(c => c.id === colId);
    return conf?.width || colDef?.width || 100;
  };

  const calcularEstilo = (colId) => {
    const w = getWidth(colId);
    return { width: w, minWidth: w, maxWidth: w };
  };

  const renderColGroup = () => (
    <colgroup>
      {columnasTabla.map((col) => {
        const w = getWidth(col.id);
        return <col key={col.id} style={{ width: w, minWidth: w, maxWidth: w }} />;
      })}
    </colgroup>
  );

  const tableMinWidth = useMemo(
    () => columnasTabla.reduce((acc, col) => acc + getWidth(col.id), 0),
    [columnasTabla, colConfig]
  );
  const effectiveTableWidth = tablaTieneOverflowX ? `${tableMinWidth}px` : "100%";

  const syncTablaScrollMetrics = useCallback(() => {
    const body = tablaScrollRef.current;
    if (!body) return;
    const hasOverflow = body.scrollWidth > body.clientWidth + 2;
    setTablaTieneOverflowX(hasOverflow);

    const header = tablaHeaderScrollRef.current;
    const headerInner = tablaHeaderInnerRef.current;
    const footer = tablaScrollFooterRef.current;
    const inner = tablaScrollFooterInnerRef.current;
    if (header && headerInner) {
      headerInner.style.width = `${body.scrollWidth}px`;
      if (Math.abs(header.scrollLeft - body.scrollLeft) > 1) {
        header.scrollLeft = body.scrollLeft;
      }
    }
    if (!footer || !inner) return;

    inner.style.width = `${body.scrollWidth}px`;
    if (Math.abs(footer.scrollLeft - body.scrollLeft) > 1) {
      footer.scrollLeft = body.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    syncTablaScrollMetrics();
  }, [syncTablaScrollMetrics, tableMinWidth, filtradas.length, cargando, resizing]);

  useLayoutEffect(() => {
    if (tablaTieneOverflowX) syncTablaScrollMetrics();
  }, [tablaTieneOverflowX, syncTablaScrollMetrics]);

  useEffect(() => {
    const onResize = () => syncTablaScrollMetrics();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [syncTablaScrollMetrics]);

  const syncHorizontalScroll = useCallback((source) => {
    const body = tablaScrollRef.current;
    const header = tablaHeaderScrollRef.current;
    const footer = tablaScrollFooterRef.current;
    if (!body || !header || !footer || tablaScrollSyncRef.current) return;
    tablaScrollSyncRef.current = true;
    const origin =
      source === "header" ? header :
      source === "footer" ? footer :
      body;
    const left = origin.scrollLeft;
    if (source !== "body") body.scrollLeft = left;
    if (source !== "header") header.scrollLeft = left;
    if (source !== "footer") footer.scrollLeft = left;
    requestAnimationFrame(() => {
      tablaScrollSyncRef.current = false;
    });
  }, []);

  const formatFecha = (f) => {
    if (!f) return "-";
    const d = new Date(f);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatPrecio = (v) => {
    if (!v) return "0 €";
    return Number(v).toLocaleString("es-ES") + " €";
  };

  const renderCelda = (p, colId) => {
    switch (colId) {
      case "select":
        return <input type="checkbox" checked={seleccionadas.includes(p.id)} onChange={() => toggleSeleccion(p.id)} className="rounded" />;
      case "publicada":
        return <span style={{ color: p.publicar_web ? t.accentGreen : t.textMuted }}>{p.publicar_web ? "✓" : "✗"}</span>;
      case "imagenes":
        return (
          <div className="relative w-full h-full" onClick={(e) => e.stopPropagation()}>
            {p.fotos && p.fotos[0] ? (
              <img src={p.fotos[0]?.startsWith("http") ? p.fotos[0] : "http://localhost:4000" + p.fotos[0]} alt=""
                className="w-full h-full object-cover cursor-pointer block"
                style={{ height: "90px" }}
                onClick={() => abrirVisor(p.fotos)} />
            ) : (
              <div className="w-full flex items-center justify-center text-xs" style={{ minHeight: "70px", color: t.textMuted, background: isDark ? t.bgPanel : "#e2e8f0" }}>—</div>
            )}
            {(() => {
              const est = etiquetaEstadoPropiedad(p, carteraFiltro);
              if (!est) return null;
              return (
                <span
                  className="absolute bottom-1 left-1 text-[10px] font-bold rounded px-1.5 py-0.5 leading-tight pointer-events-none shadow-sm"
                  style={{ background: est.bg, color: est.color || "#fff", maxWidth: "calc(100% - 8px)" }}
                >
                  {est.text}
                </span>
              );
            })()}
            <div className="absolute top-1 right-1 flex flex-col items-end gap-0.5">
              {p.num_fotos > 0 && (
                <span className="bg-black/60 text-white text-[10px] font-bold rounded px-1 py-0.5 leading-none pointer-events-none">
                  {p.num_fotos}
                </span>
              )}
              {p.num_videos > 0 && (
                <button
                  type="button"
                  title="Ver vídeos (flechas ← → para cambiar)"
                  onClick={(e) => abrirVisorVideos(p, e)}
                  className="bg-red-600 hover:bg-red-500 active:bg-red-700 text-white text-[10px] font-bold rounded px-1 py-0.5 leading-none shadow-sm cursor-pointer border-0 font-inherit"
                >
                  {p.num_videos}
                </button>
              )}
            </div>
          </div>
        );
      case "operaciones":
        return (
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); abrirFichaResumen(p); }}
              className="absolute -top-1 right-0 p-0.5" style={{ color: t.textMuted }} title="Ficha resumen">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
            </button>
            <div className="flex flex-col text-xs leading-tight" style={{ color: t.textSecondary }}>
              <span>
                <span style={{ color: p.operacion === "Venta" ? t.accentGreen : t.accentRed }}>{p.operacion === "Venta" ? "✓" : "✗"}</span>
                {" "}Venta {p.operacion === "Venta" && <span className="font-mono" style={{ color: isDark ? "#fbbf24" : "#2563eb" }}>{p.referencia}</span>}
              </span>
              <span>
                <span style={{ color: p.operacion === "Alquiler" ? t.accentGreen : t.accentRed }}>{p.operacion === "Alquiler" ? "✓" : "✗"}</span>
                {" "}Alquiler {p.operacion === "Alquiler" && <span className="font-mono" style={{ color: t.accentBlue }}>{p.referencia}</span>}
              </span>
              <span>
                <span style={{ color: p.operacion === "Alquiler vacacional" ? t.accentGreen : t.accentRed }}>{p.operacion === "Alquiler vacacional" ? "✓" : "✗"}</span>
                {" "}Vacacional
              </span>
            </div>
          </div>
        );
      case "tipo": {
        const isEditing = editandoCampo?.propId === p.id && editandoCampo?.campo === "tipo";
        if (isEditing) return (
          <div onClick={e => e.stopPropagation()}>
            <select autoFocus defaultValue={p.tipo_inmueble_id || ""}
              onBlur={() => setTimeout(() => setEditandoCampo(null), 200)}
              onChange={async (e) => {
                const id = e.target.value || null;
                setEditandoCampo(null);
                try {
                  const res = await api.patch(`/propiedades/${p.id}/tipo`, { tipo_inmueble_id: id });
                  setPropiedades(prev => prev.map(x => x.id === p.id ? { ...x, tipo_inmueble_id: res.data.tipo_inmueble_id, tipo: res.data.tipo } : x));
                } catch {}
              }}
              style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: `1px solid ${t.accentBlue}`, background: t.bgCard, color: t.textPrimary, outline: "none", width: "100%", maxWidth: 160 }}>
              <option value="">— Sin tipo —</option>
              {tiposInmueble.map(ti => <option key={ti.id} value={ti.id}>{ti.nombre}</option>)}
            </select>
          </div>
        );
        return <span className="text-sm" title={puedeEditarInline ? "Clic para cambiar" : undefined} onClick={e => { e.stopPropagation(); if (puedeEditarInline) setEditandoCampo({ propId: p.id, campo: "tipo" }); }} style={{ color: t.textPrimary, cursor: puedeEditarInline ? "pointer" : "default" }}>{p.tipo || <span style={{ color: t.textMuted, fontStyle: "italic" }}>Sin tipo</span>}</span>;
      }
      case "precio_venta": {
        const isEditing = editandoCampo?.propId === p.id && editandoCampo?.campo === "precio_venta";
        if (isEditing) {
          const guardarPrecio = async (raw) => {
            setEditandoCampo(null);
            const prev =
              p.precio_venta != null && p.precio_venta !== ""
                ? Math.round(Number(p.precio_venta))
                : null;
            const num = parsePrecioVentaDesdeInput(raw);
            if (num != null && (Number.isNaN(num) || num < 0)) return;
            if (num === prev) return;
            try {
              const res = await api.patch(`/propiedades/${p.id}/precio-venta`, { precio_venta: num });
              setPropiedades((prev) =>
                prev.map((x) => (x.id === p.id ? { ...x, precio_venta: res.data.precio_venta } : x))
              );
            } catch {}
          };
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                type="text"
                defaultValue={precioVentaParaInput(p.precio_venta)}
                placeholder="€"
                onBlur={(e) => guardarPrecio(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") guardarPrecio(e.currentTarget.value);
                  if (e.key === "Escape") setEditandoCampo(null);
                }}
                style={{
                  fontSize: 12,
                  padding: "3px 6px",
                  borderRadius: 6,
                  border: `1px solid ${t.accentBlue}`,
                  background: t.bgCard,
                  color: t.textPrimary,
                  outline: "none",
                  width: "100%",
                  maxWidth: 120,
                }}
              />
            </div>
          );
        }
        return (
          <span
            className="text-sm font-medium"
            title={puedeEditarInline ? "Clic para cambiar" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (puedeEditarInline) setEditandoCampo({ propId: p.id, campo: "precio_venta" });
            }}
            style={{ color: t.textPrimary, cursor: puedeEditarInline ? "pointer" : "default" }}
          >
            {p.precio_venta ? Number(p.precio_venta).toLocaleString("es-ES") + " €" : "-"}
          </span>
        );
      }
      case "localidad": {
        const isEditing = editandoCampo?.propId === p.id && editandoCampo?.campo === "localidad";
        const valorPobSelect = (() => {
          if (p.poblacion_id == null || p.poblacion_id === "") return "";
          const match = poblaciones.find(
            (pb) => parseInt(String(pb.id), 10) === parseInt(String(p.poblacion_id), 10)
          );
          return match ? String(match.id) : String(p.poblacion_id).padStart(5, "0");
        })();
        if (isEditing) {
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <select
                autoFocus
                defaultValue={valorPobSelect}
                onBlur={() => setTimeout(() => setEditandoCampo(null), 200)}
                onChange={async (e) => {
                  const raw = e.target.value || null;
                  setEditandoCampo(null);
                  try {
                    const pobIdNum = raw ? parseInt(String(raw), 10) : null;
                    const res = await api.patch(`/propiedades/${p.id}/localidad`, { poblacion_id: pobIdNum });
                    const nombrePob =
                      res.data?.poblacion ||
                      poblaciones.find((pb) => String(pb.id) === String(raw))?.nombre ||
                      null;
                    setPropiedades((prev) =>
                      prev.map((x) =>
                        x.id === p.id
                          ? {
                              ...x,
                              poblacion_id: res.data?.poblacion_id ?? pobIdNum,
                              poblacion: nombrePob,
                              poblacion_nombre: nombrePob,
                            }
                          : x
                      )
                    );
                  } catch (err) {
                    const msg = err.response?.data?.error || err.message || "Error al guardar localidad";
                    console.error("Error al guardar localidad:", msg);
                    dialogs.alert(msg);
                  }
                }}
                style={{
                  fontSize: 12,
                  padding: "3px 6px",
                  borderRadius: 6,
                  border: `1px solid ${t.accentBlue}`,
                  background: t.bgCard,
                  color: t.textPrimary,
                  outline: "none",
                  width: "100%",
                  maxWidth: 200,
                }}
              >
                <option value="">— Sin localidad —</option>
                {poblaciones.map((pb) => (
                  <option key={pb.id} value={pb.id}>
                    {pb.nombre}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        {
          const nombrePob = nombrePoblacionDisplay(p, poblaciones);
          return (
            <span
              className="text-sm"
              title={nombrePob ? `${nombrePob}${puedeEditarInline ? " (clic para cambiar)" : ""}` : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (puedeEditarInline) setEditandoCampo({ propId: p.id, campo: "localidad" });
              }}
              style={{
                color: t.textPrimary,
                cursor: puedeEditarInline ? "pointer" : "default",
                display: "block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nombrePob || (
                <span style={{ color: t.textMuted, fontStyle: "italic" }}>Sin localidad</span>
              )}
            </span>
          );
        }
      }
      case "area": {
        const isEditing = editandoCampo?.propId === p.id && editandoCampo?.campo === "zona";
        if (isEditing) return (
          <div onClick={e => e.stopPropagation()}>
            <select autoFocus defaultValue={p.zona_id || ""}
              onBlur={() => setTimeout(() => setEditandoCampo(null), 200)}
              onChange={async (e) => {
                const id = e.target.value || null;
                setEditandoCampo(null);
                try {
                  const res = await api.patch(`/propiedades/${p.id}/zona`, { zona_id: id });
                  setPropiedades(prev => prev.map(x => x.id === p.id ? { ...x, zona_id: res.data.zona_id, zona: res.data.zona } : x));
                } catch {}
              }}
              style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: `1px solid ${t.accentBlue}`, background: t.bgCard, color: t.textPrimary, outline: "none", width: "100%", maxWidth: 160 }}>
              <option value="">— Sin zona —</option>
              {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
            </select>
          </div>
        );
        return <span className="text-sm" title={puedeEditarInline ? "Clic para cambiar" : undefined} onClick={e => { e.stopPropagation(); if (puedeEditarInline) setEditandoCampo({ propId: p.id, campo: "zona" }); }} style={{ color: t.textPrimary, cursor: puedeEditarInline ? "pointer" : "default" }}>{p.zona || <span style={{ color: t.textMuted, fontStyle: "italic" }}>Sin zona</span>}</span>;
      }
      case "habitaciones": {
        const isEditing = editandoCampo?.propId === p.id && editandoCampo?.campo === "habitaciones";
        if (isEditing) {
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <select
                autoFocus
                defaultValue={p.num_dormitorios != null ? String(p.num_dormitorios) : ""}
                onBlur={() => setTimeout(() => setEditandoCampo(null), 200)}
                onChange={async (e) => {
                  const v = e.target.value;
                  const num = v === "" ? null : parseInt(v, 10);
                  setEditandoCampo(null);
                  try {
                    const res = await api.patch(`/propiedades/${p.id}/habitaciones`, { num_dormitorios: num });
                    setPropiedades((prev) =>
                      prev.map((x) => (x.id === p.id ? { ...x, num_dormitorios: res.data.num_dormitorios } : x))
                    );
                  } catch {}
                }}
                style={{
                  fontSize: 12,
                  padding: "3px 6px",
                  borderRadius: 6,
                  border: `1px solid ${t.accentBlue}`,
                  background: t.bgCard,
                  color: t.textPrimary,
                  outline: "none",
                  width: "100%",
                  maxWidth: 72,
                }}
              >
                <option value="">—</option>
                {Array.from({ length: 16 }, (_, i) => (
                  <option key={i} value={String(i)}>
                    {i}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        return (
          <span
            className="text-sm text-center block"
            title={puedeEditarInline ? "Clic para cambiar" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (puedeEditarInline) setEditandoCampo({ propId: p.id, campo: "habitaciones" });
            }}
            style={{ color: t.textPrimary, cursor: puedeEditarInline ? "pointer" : "default" }}
          >
            {p.num_dormitorios ?? "-"}
          </span>
        );
      }
      case "piscina": {
        const isEditing = editandoCampo?.propId === p.id && editandoCampo?.campo === "piscina";
        const valorSelect = p.eq_piscina_priv && p.eq_piscina_comun ? "ambas" : p.eq_piscina_priv ? "priv" : p.eq_piscina_comun ? "com" : "ninguna";
        if (isEditing) {
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <select
                autoFocus
                defaultValue={valorSelect}
                onBlur={() => setTimeout(() => setEditandoCampo(null), 200)}
                onChange={async (e) => {
                  const v = e.target.value;
                  let priv = false;
                  let com = false;
                  if (v === "priv") priv = true;
                  if (v === "com") com = true;
                  if (v === "ambas") {
                    priv = true;
                    com = true;
                  }
                  setEditandoCampo(null);
                  try {
                    const res = await api.patch(`/propiedades/${p.id}/piscina`, {
                      eq_piscina_priv: priv,
                      eq_piscina_comun: com,
                    });
                    setPropiedades((prev) =>
                      prev.map((x) =>
                        x.id === p.id
                          ? {
                              ...x,
                              eq_piscina_priv: res.data.eq_piscina_priv,
                              eq_piscina_comun: res.data.eq_piscina_comun,
                            }
                          : x
                      )
                    );
                  } catch {}
                }}
                style={{
                  fontSize: 12,
                  padding: "3px 6px",
                  borderRadius: 6,
                  border: `1px solid ${t.accentBlue}`,
                  background: t.bgCard,
                  color: t.textPrimary,
                  outline: "none",
                  width: "100%",
                  maxWidth: 100,
                }}
              >
                <option value="ninguna">—</option>
                <option value="priv">Priv.</option>
                <option value="com">Com.</option>
                <option value="ambas">Priv. + com.</option>
              </select>
            </div>
          );
        }
        return (
          <span
            className="text-sm"
            title={puedeEditarInline ? "Clic para cambiar" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (puedeEditarInline) setEditandoCampo({ propId: p.id, campo: "piscina" });
            }}
            style={{ color: t.textPrimary, cursor: puedeEditarInline ? "pointer" : "default" }}
          >
            {p.eq_piscina_priv ? "Priv" : ""}
            {p.eq_piscina_priv && p.eq_piscina_comun ? " / " : ""}
            {p.eq_piscina_comun ? "Com" : ""}
            {!p.eq_piscina_priv && !p.eq_piscina_comun ? "-" : ""}
          </span>
        );
      }
      case "propietario": {
        const isEditing = puedeEditarInline && editandoCampo?.propId === p.id && editandoCampo?.campo === "propietario";
        if (isEditing) {
          const guardarProp = async ({ nombre_completo, contacto_id } = {}) => {
            setEditandoCampo(null);
            const body = isUuid(contacto_id)
              ? { contacto_id }
              : { nombre_completo: String(nombre_completo ?? "").trim() };
            try {
              const res = await api.patch(`/propiedades/${p.id}/propietario-principal`, body);
              setPropiedades((prev) =>
                prev.map((x) =>
                  x.id === p.id
                    ? {
                        ...x,
                        propietario: res.data.propietario || "",
                        propietario_contacto_id: res.data.propietario_contacto_id ?? null,
                      }
                    : x
                )
              );
            } catch (e) {
              await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo asignar el propietario");
            }
          };
          return (
            <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 160, maxWidth: 260 }}>
              <PropietarioBuscarOCrear
                compact
                mostrarEtiquetaBuscar={false}
                mostrarSeparador={false}
                isDark={isDark}
                initialValue={p.propietario || ""}
                placeholder="Buscar propietario..."
                buscadorProps={{
                  autoFocus: true,
                  tipo: "propietario",
                  style: {
                    border: `1px solid ${t.accentBlue}`,
                    background: t.bgCard,
                    color: t.textPrimary,
                  },
                  onBlurText: (text) => guardarProp({ nombre_completo: text }),
                  onCancel: () => setEditandoCampo(null),
                }}
                onSeleccionar={(c) => {
                  const label = [c.nombre, c.apellidos].filter(Boolean).join(" ").trim();
                  guardarProp({ contacto_id: c.id, nombre_completo: label });
                }}
                onAbrirCrear={(texto) => {
                  setEditandoCampo(null);
                  setModalPropietarioTabla({
                    propiedadId: p.id,
                    propietarioEditar: null,
                    textoBusqueda: texto,
                  });
                }}
              />
            </div>
          );
        }
        const label = (p.propietario || "").trim();
        if (!label && !p.propietario_contacto_id) {
          return (
            <span
              className="text-sm"
              title={puedeEditarInline ? "Buscar propietario existente" : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (puedeEditarInline) abrirBuscadorPropietarioTabla(p);
              }}
              style={{
                color: t.textMuted,
                fontStyle: "italic",
                cursor: puedeEditarInline ? "pointer" : "default",
                textDecoration: puedeEditarInline ? "underline" : "none",
                textUnderlineOffset: 2,
              }}
            >
              —
            </span>
          );
        }
        return (
          <span className="text-sm" style={{ color: t.textPrimary }}>
            {label}
          </span>
        );
      }
      case "agente":
        if (puedeEditarInline && editandoAgenteId === p.id) {
          return (
            <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
              <select
                autoFocus
                defaultValue={p.agente_id || ""}
                onBlur={() => setTimeout(() => setEditandoAgenteId(null), 200)}
                onChange={async (e) => {
                  const nuevoId = e.target.value || null;
                  setEditandoAgenteId(null);
                  try {
                    const res = await api.patch(`/propiedades/${p.id}/agente`, { agente_id: nuevoId });
                    setPropiedades(prev => prev.map(x =>
                      x.id === p.id
                        ? { ...x, agente_id: res.data.agente_id, agente_nombre: res.data.agente_nombre }
                        : x
                    ));
                  } catch {}
                }}
                style={{
                  fontSize: 12, padding: "3px 6px", borderRadius: 6,
                  border: `1px solid ${t.accentBlue}`, background: t.bgCard,
                  color: t.textPrimary, outline: "none", width: "100%", maxWidth: 160,
                }}>
                <option value="">— Sin agente —</option>
                {agentes.map(a => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
          );
        }
        return (
          <span
            className="text-sm"
            title={puedeEditarInline ? "Clic para cambiar agente" : undefined}
            onClick={e => { e.stopPropagation(); if (puedeEditarInline) setEditandoAgenteId(p.id); }}
            style={{ color: t.textPrimary, cursor: puedeEditarInline ? "pointer" : "default" }}>
            {p.agente_nombre || <span style={{ color: t.textMuted, fontStyle: "italic" }}>Sin agente</span>}
          </span>
        );
      case "direccion":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{p.direccion_completa || "-"}</span>;
      case "exclusiva":
        return (
          <span
            className="text-sm font-medium text-center block"
            style={{ color: p.exclusiva ? t.accentGreen : t.textMuted }}
          >
            {p.exclusiva ? "Sí" : "No"}
          </span>
        );
      case "exclusiva_hasta":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {p.exclusiva_hasta
              ? new Date(p.exclusiva_hasta).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </span>
        );
      case "fecha_alta":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{p.fecha_alta ? new Date(p.fecha_alta).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</span>;
      case "fecha_actualizacion":
        return (
          <span className="text-sm" style={{ color: t.textPrimary }}>
            {p.updated_at
              ? new Date(p.updated_at).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </span>
        );
      case "hist_leads_total":
        return (
          <span className="text-sm text-center block tabular-nums" style={{ color: t.textPrimary }}>
            {Number(p.hist_leads_total) > 0 ? p.hist_leads_total : "0"}
          </span>
        );
      case "hist_citas_total":
        return (
          <span className="text-sm text-center block tabular-nums" style={{ color: t.textPrimary }}>
            {Number(p.hist_citas_total) > 0 ? p.hist_citas_total : "0"}
          </span>
        );
      case "ano_construccion":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {p.ano_construccion ?? "—"}
          </span>
        );
      case "num_aseos":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {p.num_aseos ?? "—"}
          </span>
        );
      case "num_banos":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {p.num_banos ?? "—"}
          </span>
        );
      case "coordenadas": {
        const lat = p.latitud != null && p.latitud !== "" ? Number(p.latitud) : null;
        const lng = p.longitud != null && p.longitud !== "" ? Number(p.longitud) : null;
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
          return (
            <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-1">
              <span className="text-sm" style={{ color: t.textMuted }}>—</span>
              <BotonesVisorUbicacion propiedad={p} compact />
            </div>
          );
        }
        const label = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        return (
          <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-1 max-w-full">
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Abrir en Google Maps: ${label}`}
              className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-mono"
              style={{ color: t.accentBlue, textDecoration: "underline" }}
            >
              {label}
            </a>
            <BotonesVisorUbicacion propiedad={p} compact />
          </div>
        );
      }
      case "parcela":
        return (
          <span className="text-sm text-center block" style={{ color: t.textPrimary }}>
            {p.parcela ?? "—"}
          </span>
        );
      case "m2_construidos":
        return (
          <span className="text-sm text-center block tabular-nums whitespace-nowrap" style={{ color: t.textPrimary }}>
            {p.m2_construidos != null ? `${Number(p.m2_construidos).toLocaleString("es-ES")} m²` : "—"}
          </span>
        );
      case "vistas":
        return (
          <span className="text-sm truncate block max-w-[140px]" title={p.vistas || ""} style={{ color: t.textPrimary }}>
            {p.vistas || <span style={{ color: t.textMuted }}>—</span>}
          </span>
        );
      case "urbanizacion":
        return (
          <span className="text-sm truncate block max-w-[150px]" title={p.urbanizacion || ""} style={{ color: t.textPrimary }}>
            {p.urbanizacion || <span style={{ color: t.textMuted }}>—</span>}
          </span>
        );
      case "actividad": {
        const act = p.actividad;
        if (!act) return <span style={{ color: t.textMuted }}>—</span>;
        const chip = (emoji, label, data) => {
          const tiene = data.total > 0;
          return (
            <button
              type="button"
              title={`${label}: ${data.total} en total · última ${formatFecha(data.ultima)}`}
              onClick={(e) => {
                e.stopPropagation();
                setModalActividad({ abierto: true, propiedad: p });
              }}
              className="text-left text-[11px] px-2 py-1 rounded-md w-full"
              style={{
                border: `1px solid ${tiene ? t.border : "transparent"}`,
                background: tiene ? (isDark ? "rgba(255,255,255,0.04)" : "#f8fafc") : "transparent",
                color: tiene ? t.textPrimary : t.textMuted,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <span style={{ marginRight: 4 }}>{emoji}</span>
              <span style={{ fontWeight: 600 }}>{label}</span>
              <span style={{ color: t.textMuted, marginLeft: 6 }}>
                {tiene ? formatFecha(data.ultima) : "sin registro"}
              </span>
            </button>
          );
        };
        return (
          <div className="flex flex-col gap-1 min-w-[200px]" onClick={(e) => e.stopPropagation()}>
            {chip("🏠", "Visitas", act.visitas)}
            {chip("📥", "Leads", act.leads)}
            {chip("💰", "Ofertas", act.ofertas)}
            <button
              type="button"
              className="text-[10px] font-semibold mt-0.5"
              style={{ color: t.accentBlue, background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
              onClick={(e) => {
                e.stopPropagation();
                setModalActividad({ abierto: true, propiedad: p });
              }}
            >
              Ver detalle →
            </button>
          </div>
        );
      }
      default:
        return "-";
    }
  };

  return (
    <div className="min-h-screen" style={{ background: t.bgApp }}>
      <style>{`
        .tabla-scroll-hidden {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .tabla-scroll-hidden::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }
      `}</style>
      <header
        ref={headerStickyRef}
        className="px-6 pt-3 pb-2 flex flex-col gap-1.5"
        style={{
          position: "sticky",
          top: 54,
          zIndex: 40,
          background: t.bgCard,
          borderBottom: `1px solid ${t.border}`,
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="font-bold text-lg" style={{ color: t.textPrimary }}>Propiedades</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <div>
            <button
              ref={colConfigBtnRef}
              type="button"
              onClick={() => setMostrarColConfig((v) => !v)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ border: `1px solid ${t.border}`, color: t.textSecondary, background: t.bgCard }}
            >
              ⚙️ Columnas
            </button>
            {mostrarColConfig && colMenuPos && createPortal(
              <div
                ref={colConfigPanelRef}
                className="rounded-lg shadow-xl flex flex-col"
                style={{
                  position: "fixed",
                  top: colMenuPos.top,
                  left: colMenuPos.left,
                  width: colMenuPos.width,
                  maxHeight: colMenuPos.maxHeight,
                  zIndex: 10000,
                  background: t.bgCard,
                  border: `1px solid ${t.border}`,
                }}
              >
                <p
                  className="text-xs font-bold px-3 pt-3 pb-2 uppercase"
                  style={{ color: t.textMuted, borderBottom: `1px solid ${t.border}` }}
                >
                  Columnas visibles (arrastra para reordenar)
                </p>
                <div
                  className="overflow-y-auto px-2 py-1"
                  style={{ maxHeight: colMenuPos.maxHeight - 88 }}
                >
                  {colConfig.filter(cc => cc.id !== "select").map(cc => {
                    const colDef = TODAS_COLUMNAS.find(c => c.id === cc.id);
                    if (!colDef) return null;
                    return (
                      <div
                        key={cc.id}
                        draggable
                        onDragStart={(e) => onDragStartCol(e, cc.id)}
                        onDragOver={(e) => onDragOverCol(e, cc.id)}
                        onDrop={() => onDropCol(cc.id)}
                        onDragEnd={() => { setDragCol(null); setDragOver(null); }}
                        className="flex items-center gap-2 py-1.5 px-1 rounded cursor-grab active:cursor-grabbing"
                        style={{ background: dragOver === cc.id ? t.accentBlueLight : "transparent" }}
                      >
                        <span className="text-xs" style={{ color: t.textMuted }}>⠿</span>
                        <input
                          type="checkbox"
                          checked={cc.visible !== false}
                          onChange={() => toggleColumna(cc.id)}
                          className="rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm" style={{ color: t.textSecondary }}>{colDef.label}</span>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setMostrarColConfig(false)}
                  className="py-2 w-full text-xs font-semibold"
                  style={{
                    color: t.accentBlue,
                    borderTop: `1px solid ${t.border}`,
                    background: isDark ? t.bgPanel : "#f8fafc",
                  }}
                >
                  Cerrar
                </button>
              </div>,
              document.body
            )}
          </div>
        </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" style={{ paddingLeft: 4 }}>
          <p className="text-xs whitespace-nowrap leading-none" style={{ color: t.textMuted, margin: 0 }}>
            {propiedades.length.toLocaleString("es-ES")} de {totalResultados.toLocaleString("es-ES")}{" "}
            {totalResultados === 1 ? "resultado" : "resultados"}
            {" · "}
            {FILTROS_CARTERA.find((f) => f.id === metaCartera)?.label || metaCartera}
            {pocaActividadEtiqueta ? ` · Poca actividad (${pocaActividadEtiqueta}${pocaActividadOrdenMenos ? ", menos activas primero" : ""})` : ""}
            {exclusivasFiltro ? " · Exclusivas" : ""}
          </p>
          <div style={{ width: 14, flexShrink: 0 }} />
          {filtrosCarteraVisibles.map((f) => {
            const activo = carteraFiltro === f.id;
            return (
              <button
                key={f.id}
                type="button"
                title={f.hint}
                onClick={() => cambiarCartera(f.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{
                  border: `1px solid ${activo ? t.accentGreen : t.border}`,
                  background: activo ? (isDark ? "rgba(34,197,94,0.22)" : t.accentGreenLight) : t.bgInput,
                  color: activo ? t.accentGreen : t.textSecondary,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            );
          })}
          <button
            type="button"
            title="Propiedades con llaves (sin vendidas, alquiladas ni eliminadas)"
            onClick={() => navigate("/propiedades/llaves-fuera")}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              border: `1px solid ${t.border}`,
              background: t.bgInput,
              color: t.textSecondary,
              cursor: "pointer",
            }}
          >
            🔑 Llaves
          </button>
          {puede("prop_list_poca_actividad", "ver") && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setPocaActividadPanel((v) => !v)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{
                border: `1px solid ${pocaActividadDesde || pocaActividadPanel ? "#d97706" : t.border}`,
                background: pocaActividadDesde
                  ? (isDark ? "rgba(217,119,6,0.22)" : "rgba(217,119,6,0.12)")
                  : pocaActividadPanel
                    ? (isDark ? "rgba(217,119,6,0.15)" : "rgba(217,119,6,0.08)")
                    : t.bgInput,
                color: pocaActividadDesde || pocaActividadPanel ? "#d97706" : t.textSecondary,
                cursor: "pointer",
              }}
            >
              Poca actividad{pocaActividadEtiqueta ? ` · ${pocaActividadEtiqueta}` : ""}
            </button>
            {pocaActividadPanel && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setPocaActividadPanel(false)} aria-hidden />
                <div
                  className="absolute left-0 top-full mt-1 z-30 rounded-lg shadow-lg p-3"
                  style={{ background: t.bgCard, border: `1px solid ${t.border}`, minWidth: 280 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: t.textMuted }}>
                    Poca actividad desde…
                  </p>
                  <p className="text-[10px] mb-2" style={{ color: t.textMuted }}>
                    Sin visitas, leads ni ofertas en el período
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {PRESETS_POCA_ACTIVIDAD.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => aplicarPocaActividad(fechaHaceMeses(preset.months), preset.label)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium"
                        style={{
                          border: `1px solid ${pocaActividadDesde === fechaHaceMeses(preset.months) ? "#d97706" : t.border}`,
                          background: pocaActividadDesde === fechaHaceMeses(preset.months) ? "rgba(217,119,6,0.12)" : t.bgInput,
                          color: t.textPrimary,
                          cursor: "pointer",
                        }}
                      >
                        {preset.label}
                      </button>
                    ))}
                    <div
                      className="px-3 py-2 rounded-lg"
                      style={{ border: `1px solid ${t.border}`, background: t.bgInput }}
                    >
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: t.textMuted }}>
                        Otra fecha
                      </label>
                      <input
                        type="date"
                        value={pocaActividadFechaCustom}
                        max={fechaYmd()}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPocaActividadFechaCustom(v);
                          if (v) {
                            const d = new Date(v + "T12:00:00");
                            const etiqueta = d.toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            });
                            aplicarPocaActividad(v, `desde ${etiqueta}`);
                          }
                        }}
                        className="w-full px-2 py-1.5 rounded text-sm"
                        style={{
                          border: `1px solid ${t.border}`,
                          background: t.bgCard,
                          color: t.textPrimary,
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOrdenMenosActividad(!pocaActividadOrdenMenos);
                      }}
                      className="w-full text-left flex items-start gap-2 px-3 py-2 rounded-lg"
                      style={{
                        border: `1px solid ${pocaActividadOrdenMenos ? t.accentGreen : t.border}`,
                        background: pocaActividadOrdenMenos
                          ? (isDark ? "rgba(34,197,94,0.12)" : t.accentGreenLight)
                          : t.bgInput,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          marginTop: 2,
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: `2px solid ${pocaActividadOrdenMenos ? t.accentGreen : t.border}`,
                          background: pocaActividadOrdenMenos ? t.accentGreen : "transparent",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {pocaActividadOrdenMenos ? "✓" : ""}
                      </span>
                      <span className="text-sm" style={{ color: t.textPrimary }}>
                        Ordenar por menos actividad
                        <span className="block text-xs mt-0.5" style={{ color: t.textMuted }}>
                          {pocaActividadDesde
                            ? "Primero sin visitas, luego la última visita más antigua"
                            : "Elige un período arriba para aplicar el orden"}
                        </span>
                      </span>
                    </button>
                    {pocaActividadDesde && (
                      <button
                        type="button"
                        onClick={quitarPocaActividad}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm"
                        style={{ color: "#ef4444", background: "transparent", border: "none", cursor: "pointer" }}
                      >
                        Quitar filtro
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          )}
          {puede("prop_list_exclusivas", "ver") && (
          <button
            type="button"
            title="Solo propiedades en exclusiva"
            onClick={toggleFiltroExclusivas}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              border: `1px solid ${exclusivasFiltro ? t.accentGreen : t.border}`,
              background: exclusivasFiltro
                ? (isDark ? "rgba(34,197,94,0.22)" : t.accentGreenLight)
                : t.bgInput,
              color: exclusivasFiltro ? t.accentGreen : t.textSecondary,
              cursor: "pointer",
            }}
          >
            Exclusivas
          </button>
          )}
          {puede("propiedades", "ver") && (
          <button
            type="button"
            title="Cruzar todas las propiedades con clientes interesados"
            onClick={() => setModalCruceClientes(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              border: `1px solid ${t.border}`,
              background: t.bgInput,
              color: t.textSecondary,
              cursor: "pointer",
            }}
          >
            Cruce Clientes
          </button>
          )}
          {puede("prop_list_buscar", "ver") && (
          <input value={busqueda} onChange={e => onCambioBusqueda(e.target.value)}
            placeholder="Buscar por referencia, dirección, propietario, contacto, teléfono, email…"
            className="w-full md:flex-1 md:min-w-[320px] md:max-w-md px-4 py-1.5 rounded-lg text-sm focus:outline-none"
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, color: t.textPrimary, borderColor: t.border, minHeight: 32, marginLeft: 8 }} />
          )}
          {seleccionadas.length > 0 && (
            <div className="flex items-center gap-2 self-center">
              <span className="text-sm" style={{ color: t.textMuted }}>{seleccionadas.length} seleccionada{seleccionadas.length !== 1 ? "s" : ""}</span>
              <button
                type="button"
                onClick={() => setModalAcciones(true)}
                className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                style={{ background: t.accentBlue, color: "#fff" }}
              >
                Acciones
              </button>
              <button
                type="button"
                onClick={() => setSeleccionadas([])}
                className="text-xs px-2 py-1 rounded-md"
                style={{ border: `1px solid ${t.border}`, color: t.textMuted }}
              >
                Quitar selección
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="p-4" style={{ paddingBottom: tablaTieneOverflowX ? 72 : 16 }}>
        {mensaje && (
          <div className="mb-3 text-sm px-4 py-3 rounded-lg flex items-center justify-between"
            style={{ background: t.accentGreenLight, border: `1px solid ${t.accentGreenBorder}`, color: t.accentGreen }}>
            <span>✅ {mensaje}</span>
            <button onClick={() => setMensaje("")} className="text-lg" style={{ color: t.accentGreen, opacity: 0.6 }}>✕</button>
          </div>
        )}

        {cargando && !propiedades.length ? (
          <div className="text-center py-20" style={{ color: t.textMuted }}>Cargando...</div>
        ) : filtradas.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-semibold" style={{ color: t.textSecondary }}>No hay propiedades</p>
          </div>
        ) : (
          <>
          <div
            style={{
              position: "sticky",
              top: headerStickyBottom,
              zIndex: 18,
              background: t.bgCard,
              border: `2px solid ${t.border}`,
              borderBottom: "none",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
            }}
          >
            <div
              ref={tablaHeaderScrollRef}
              onScroll={() => syncHorizontalScroll("header")}
              className="tabla-scroll-hidden"
              style={{ overflowX: "auto", overflowY: "hidden" }}
            >
              <div ref={tablaHeaderInnerRef}>
                <table
                  className="border-collapse"
                  style={{
                    width: effectiveTableWidth,
                    tableLayout: "fixed",
                  }}
                >
                  {renderColGroup()}
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                      {columnasTabla.map(col => {
                        const isSortable = !!SORT_VALUE[col.id];
                        const isActive   = sortCol === col.id;
                        return (
                          <th key={col.id} style={{ ...calcularEstilo(col.id), background: isDark ? t.bgPanel : "#f1f5f9", color: t.textMuted, borderRight: `1px solid ${t.border}`, cursor: isSortable ? "pointer" : "default" }}
                            className="text-center px-3 py-2 text-xs font-bold uppercase relative select-none last:border-r-0 group"
                            onClick={() => isSortable && toggleSort(col.id)}>
                            {col.id === "select" ? (
                              <input type="checkbox" checked={seleccionadas.length === filtradas.length && filtradas.length > 0}
                                onChange={toggleTodas} className="rounded" onClick={e => e.stopPropagation()} />
                            ) : (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                {col.label}
                                {isSortable && (
                                  <span style={{
                                    opacity: isActive ? 1 : 0,
                                    transition: "opacity 0.15s",
                                    fontSize: 10,
                                    color: isActive ? t.accentBlue : t.textMuted,
                                  }}
                                  className="group-hover:opacity-100">
                                    {isActive ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                                  </span>
                                )}
                              </span>
                            )}
                            {col.id !== "select" && (
                              <div onMouseDown={(e) => { e.stopPropagation(); onMouseDown(col.id, e); }}
                                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize"
                                style={{ background: "transparent" }}
                                onMouseEnter={e => { e.currentTarget.style.background = t.accentBlue; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }} />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>
          <div
            className="rounded-xl"
            style={{
              border: `2px solid ${t.border}`,
              borderTop: "none",
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              opacity: cargando ? 0.6 : 1,
              transition: "opacity 0.15s",
              background: t.bgCard,
            }}
          >
            <div
              ref={tablaScrollRef}
              onScroll={() => syncHorizontalScroll("body")}
              className="overflow-x-auto tabla-scroll-hidden"
              style={{
                overflowX: "auto",
                overflowY: "visible",
              }}
            >
            <table
              className="border-collapse"
              style={{
                width: effectiveTableWidth,
                tableLayout: "fixed",
              }}
            >
              {renderColGroup()}
              <tbody>
                {filtradas.map(p => (
                  <tr key={p.id} className="cursor-pointer"
                    style={{ borderBottom: `1px solid ${t.border}`, background: t.bgCard }}
                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight; }}
                    onMouseLeave={e => { e.currentTarget.style.background = t.bgCard; }}
                    onClick={() => { sessionStorage.setItem("propiedades_scrollY", window.scrollY); navigate("/propiedades/" + p.id + "/editar", { state: { returnTo: returnToListado } }); }}>
                    {columnasTabla.map(col => {
                      return (
                      <td key={col.id}
                        style={{
                          ...calcularEstilo(col.id),
                          borderRight: `1px solid ${t.border}`,
                          ...(col.id === "propietario" && puedeEditarInline ? { cursor: "pointer" } : {}),
                        }}
                        className={`${col.id === "imagenes" ? "p-0" : "px-3 py-2"} align-middle last:border-r-0`}
                        onClick={
                          col.id === "select" || col.id === "actividad"
                            ? (e) => e.stopPropagation()
                            : col.id === "propietario" && puedeEditarInline
                              ? (e) => {
                                  e.stopPropagation();
                                  if (editandoCampo?.propId === p.id && editandoCampo?.campo === "propietario") return;
                                  abrirModalPropietarioTabla(p);
                                }
                              : undefined
                        }>
                        {renderCelda(p, col.id)}
                      </td>
                    );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          </>
        )}
      {totalPages > 1 && !busqueda && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", padding: "20px" }}>
          <button
            onClick={() => cargar(pageActual - 1)}
            disabled={pageActual === 1}
            style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #444", background: pageActual === 1 ? "#222" : "#333", color: pageActual === 1 ? "#666" : "#fff", cursor: pageActual === 1 ? "default" : "pointer" }}>
            ‹ Anterior
          </button>
          <span style={{ color: "#aaa", fontSize: "14px" }}>
            Página {pageActual} de {totalPages}
          </span>
          <button
            onClick={() => cargar(pageActual + 1)}
            disabled={pageActual === totalPages}
            style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #444", background: pageActual === totalPages ? "#222" : "#333", color: pageActual === totalPages ? "#666" : "#fff", cursor: pageActual === totalPages ? "default" : "pointer" }}>
            Siguiente ›
          </button>
        </div>
      )}
      </main>

      {tablaTieneOverflowX && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 45,
            background: isDark ? "rgba(15,23,42,0.96)" : "rgba(248,250,252,0.98)",
            borderTop: `1px solid ${t.border}`,
            boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
            padding: "6px 12px 8px",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            ref={tablaScrollFooterRef}
            onScroll={() => syncHorizontalScroll("footer")}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: 14,
            }}
          >
            <div ref={tablaScrollFooterInnerRef} style={{ height: 1 }} />
          </div>
        </div>
      )}

      {fichaResumen.abierta && fichaResumen.datos && (
        <FichaResumenModal
          propiedad={fichaResumen.propiedad}
          datos={fichaResumen.datos}
          theme={theme}
          onClose={() => setFichaResumen({ abierta: false, propiedad: null, datos: null })}
        />
      )}
      
      <ModalAccionesSeleccionPropiedades
        isOpen={modalAcciones}
        onClose={() => setModalAcciones(false)}
        count={seleccionadas.length}
        onAction={onAccionSeleccion}
        carteraFiltro={carteraFiltro}
      />

      <ModalMotivoBajaPropiedad
        isOpen={!!modalMotivoBaja}
        accionId={modalMotivoBaja?.accionId}
        count={seleccionadas.length}
        onClose={() => setModalMotivoBaja(null)}
        onConfirm={(motivo) => ejecutarBajaConMotivo(modalMotivoBaja?.accionId, motivo)}
      />

      <ModalRepublicarPortales
        isOpen={!!modalPortalesPrevios?.length}
        items={modalPortalesPrevios || []}
        onClose={() => setModalPortalesPrevios(null)}
        onDone={() => {
          setModalPortalesPrevios(null);
          setMensaje("Portales republicados");
          setTimeout(() => setMensaje(""), 4000);
        }}
      />

      {modalWA && (
        <ModalWhatsApp
          isOpen={modalWA}
          allowManualRecipient
          onClose={() => { setModalWA(false); setContactoInteresadoWA(null); }}
          onSend={() => { setModalWA(false); setContactoInteresadoWA(null); }}
          contact={contactoInteresadoWA
            ? { nombre: contactoInteresadoWA.nombre, telefono: contactoInteresadoWA.telefono, iniciales: contactoInteresadoWA.nombre?.[0] || "?" }
            : { nombre: "", telefono: "", iniciales: "?" }
          }
          properties={mapPropiedadesParaEnvio(propsSeleccionadas)}
          idioma="ES"
        />
      )}

      {modalMail && (
        <ModalEmail
          isOpen={modalMail}
          allowManualRecipient
          onClose={() => { setModalMail(false); setContactoInteresadoEmail(null); }}
          onSend={() => { setModalMail(false); setContactoInteresadoEmail(null); }}
          contact={contactoInteresadoEmail
            ? { nombre: contactoInteresadoEmail.nombre, email: contactoInteresadoEmail.email, iniciales: contactoInteresadoEmail.nombre?.[0] || "?" }
            : { nombre: "", email: "", iniciales: "?" }
          }
          properties={mapPropiedadesParaEnvio(propsSeleccionadas)}
        />
      )}

      {modalLoc && (
        <ModalEnviarLocalizacion isOpen={modalLoc} onClose={() => setModalLoc(false)} propiedadIds={seleccionadas} />
      )}

      {modalPrint && (
        <ModalImprimirPropiedades isOpen={modalPrint} onClose={() => setModalPrint(false)} propiedades={propsSeleccionadas} />
      )}

      {modalInteresados && seleccionadas.length === 1 && (
        <ModalContactosInteresados
          isOpen={modalInteresados}
          onClose={() => setModalInteresados(false)}
          propiedadId={seleccionadas[0]}
          propiedadLabel={propsSeleccionadas[0]?.referencia || propsSeleccionadas[0]?.titulo || ""}
          onEnviarWA={(contacto) => {
            setContactoInteresadoWA(contacto);
            setModalInteresados(false);
            setModalWA(true);
          }}
          onEnviarEmail={(contacto) => {
            setContactoInteresadoEmail(contacto);
            setModalInteresados(false);
            setModalMail(true);
          }}
        />
      )}

      {modalCruceClientes && (
        <ModalContactosInteresados
          isOpen={modalCruceClientes}
          modoCruce
          onClose={() => setModalCruceClientes(false)}
          onEnviarWA={(contacto) => {
            setContactoInteresadoWA(contacto);
            setModalCruceClientes(false);
            setModalWA(true);
          }}
          onEnviarEmail={(contacto) => {
            setContactoInteresadoEmail(contacto);
            setModalCruceClientes(false);
            setModalMail(true);
          }}
        />
      )}

      <ModalActividadPropiedad
        open={modalActividad.abierto}
        onClose={() => setModalActividad({ abierto: false, propiedad: null })}
        propiedad={modalActividad.propiedad}
      />

      {modalPropietarioTabla && (
        <ModalCrearPropietario
          open
          propietarioEditar={modalPropietarioTabla.propietarioEditar}
          textoBusqueda={
            modalPropietarioTabla.propietarioEditar
              ? ""
              : (modalPropietarioTabla.textoBusqueda || "")
          }
          onClose={() => setModalPropietarioTabla(null)}
          onCreado={async (propietario) => {
            const propiedadId = modalPropietarioTabla.propiedadId;
            try {
              const data = await vincularPropietarioEnTabla(
                propiedadId,
                propietario.contacto_id,
                propietario.porcentaje ?? 100
              );
              const label = data.propietario
                || [propietario.nombre, propietario.apellidos].filter(Boolean).join(" ").trim();
              setPropiedades((prev) =>
                prev.map((x) =>
                  x.id === propiedadId
                    ? {
                        ...x,
                        propietario: label,
                        propietario_contacto_id: data.propietario_contacto_id ?? propietario.contacto_id,
                      }
                    : x
                )
              );
              setModalPropietarioTabla(null);
            } catch (e) {
              await dialogs.alert(
                e?.response?.data?.error || e?.message || "No se pudo vincular el propietario a la propiedad"
              );
              return false;
            }
          }}
          onActualizado={async (actualizado) => {
            const propiedadId = modalPropietarioTabla.propiedadId;
            const label = [actualizado.nombre, actualizado.apellidos].filter(Boolean).join(" ").trim();
            try {
              let data = null;
              if (isUuid(actualizado.contacto_id)) {
                data = await vincularPropietarioEnTabla(
                  propiedadId,
                  actualizado.contacto_id,
                  actualizado.porcentaje ?? 100
                );
              }
              setPropiedades((prev) =>
                prev.map((x) =>
                  x.id === propiedadId
                    ? {
                        ...x,
                        propietario: data?.propietario || label,
                        propietario_contacto_id: isUuid(data?.propietario_contacto_id)
                          ? data.propietario_contacto_id
                          : isUuid(actualizado.contacto_id)
                            ? actualizado.contacto_id
                            : x.propietario_contacto_id ?? null,
                      }
                    : x
                )
              );
              setModalPropietarioTabla(null);
            } catch (e) {
              await dialogs.alert(
                e?.response?.data?.error || e?.message || "No se pudo actualizar el propietario"
              );
              return false;
            }
          }}
          porcentajeInicial={100}
        />
      )}

      {modalRefOtraCartera && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={() => setModalRefOtraCartera(null)}
        >
          <div
            role="dialog"
            aria-labelledby="modal-ref-cartera-titulo"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: modalRefOtraCartera.multiple ? "min(480px, 100%)" : "min(400px, 100%)",
              background: t.bgCard,
              border: `1px solid ${t.border}`,
              borderRadius: 12,
              padding: "22px 24px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h3
              id="modal-ref-cartera-titulo"
              style={{ margin: "0 0 10px", fontSize: 16, fontWeight: 700, color: t.textPrimary }}
            >
              {modalRefOtraCartera.multiple
                ? (modalRefOtraCartera.filtradaEnVista ? "Coincidencias ocultas por filtros" : "Varias coincidencias en otras categorías")
                : (modalRefOtraCartera.filtradaEnVista ? "Referencia oculta por filtros" : "Referencia en otra categoría")}
            </h3>
            {modalRefOtraCartera.multiple ? (
              <>
                <p style={{ margin: "0 0 10px", fontSize: 13, color: t.textSecondary, lineHeight: 1.5 }}>
                  La búsqueda{" "}
                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: t.accentGreen }}>
                    {modalRefOtraCartera.textoBuscado || modalRefOtraCartera.referencia}
                  </span>{" "}
                  coincide con <strong style={{ color: t.textPrimary }}>{modalRefOtraCartera.total}</strong> propiedades
                  {modalRefOtraCartera.filtradaEnVista
                    ? (modalRefOtraCartera.sinVisitasActivo
                        ? ", pero algunas no aparecen por el filtro de poca actividad activo:"
                        : ", pero algunas no aparecen con los filtros actuales del listado:")
                    : ":"}
                </p>
                <ul style={{ margin: "0 0 6px", padding: 0, listStyle: "none" }}>
                  {modalRefOtraCartera.coincidencias.map((c) => (
                    <li
                      key={c.id || c.referencia}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "8px 0",
                        borderBottom: `1px solid ${t.border}`,
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: t.textSecondary }}>
                        Ref.{" "}
                        <span style={{ fontFamily: "monospace", fontWeight: 600, color: t.accentGreen }}>
                          {c.referencia}
                        </span>
                        , se encuentra en{" "}
                        <strong style={{ color: t.textPrimary }}>{c.etiqueta}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => verReferenciaEnCartera(c.referencia, c.cartera)}
                        style={{
                          flexShrink: 0,
                          padding: "5px 10px",
                          borderRadius: 6,
                          border: "none",
                          background: t.accentBlue,
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Verla en {c.etiqueta}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
            <p style={{ margin: "0 0 6px", fontSize: 14, color: t.textSecondary, lineHeight: 1.5 }}>
              {modalRefOtraCartera.filtradaEnVista ? (
                <>
                  La referencia{" "}
                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: t.accentGreen }}>
                    {modalRefOtraCartera.referencia}
                  </span>{" "}
                  está en{" "}
                  <strong style={{ color: t.textPrimary }}>{modalRefOtraCartera.etiqueta}</strong>
                  {modalRefOtraCartera.sinVisitasActivo
                    ? ", pero no aparece por el filtro de poca actividad activo."
                    : ", pero no aparece con los filtros actuales del listado."}
                </>
              ) : (
                <>
                  La referencia{" "}
                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: t.accentGreen }}>
                    {modalRefOtraCartera.referencia}
                  </span>{" "}
                  se encuentra en{" "}
                  <strong style={{ color: t.textPrimary }}>{modalRefOtraCartera.etiqueta}</strong>.
                </>
              )}
            </p>
            )}
            {!modalRefOtraCartera.filtradaEnVista && (
              <p style={{ margin: "0 0 18px", fontSize: 12, color: t.textMuted }}>
                No aparece en la vista actual ({FILTROS_CARTERA.find((f) => f.id === carteraFiltro)?.label || carteraFiltro}).
                El buscador consulta todas las categorías del inventario.
              </p>
            )}
            {modalRefOtraCartera.filtradaEnVista && (
              <p style={{ margin: "0 0 18px", fontSize: 12, color: t.textMuted }}>
                Prueba a quitar el filtro de poca actividad o cambia de pestaña si buscabas en otra categoría.
              </p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setModalRefOtraCartera(null)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  background: "transparent",
                  color: t.textSecondary,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cerrar
              </button>
              {modalRefOtraCartera.filtradaEnVista && modalRefOtraCartera.sinVisitasActivo && (
                <button
                  type="button"
                  onClick={() => {
                    setModalRefOtraCartera(null);
                    quitarPocaActividad();
                  }}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: `1px solid ${t.border}`,
                    background: t.bgInput,
                    color: t.textPrimary,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Quitar filtro actividad
                </button>
              )}
              {!modalRefOtraCartera.filtradaEnVista && !modalRefOtraCartera.multiple && (
              <button
                type="button"
                onClick={() => {
                  const dest = modalRefOtraCartera.cartera;
                  setModalRefOtraCartera(null);
                  cambiarCartera(dest);
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: t.accentBlue,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Ir a {modalRefOtraCartera.etiqueta}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {visor.abierto && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <button onClick={cerrarVisor}
            className="absolute top-4 right-4 text-white text-4xl font-light hover:text-gray-300 z-10">✕</button>

          {visor.fotos.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); visorAnterior(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10">‹</button>
          )}

          <img src={visor.fotos[visor.indice]?.startsWith("http") ? visor.fotos[visor.indice] : "http://localhost:4000" + visor.fotos[visor.indice]}
            alt="" className="max-w-[98vw] max-h-[96vh] object-contain"
            onClick={(e) => e.stopPropagation()} />

          {visor.fotos.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); visorSiguiente(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10">›</button>
          )}

          <div className="absolute bottom-4 text-white text-sm">
            {visor.indice + 1} / {visor.fotos.length}
          </div>
        </div>
      )}

      {visorVideo.abierto && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center"
          onClick={cerrarVisorVideo}
        >
          <button
            type="button"
            onClick={cerrarVisorVideo}
            className="absolute top-4 right-4 text-white text-4xl font-light hover:text-gray-300 z-10"
          >
            ✕
          </button>

          {visorVideo.cargando && (
            <p className="text-white text-sm" onClick={(e) => e.stopPropagation()}>
              Cargando vídeos…
            </p>
          )}

          {!visorVideo.cargando && visorVideo.error && (
            <p className="text-red-300 text-sm px-6 text-center max-w-md" onClick={(e) => e.stopPropagation()}>
              {visorVideo.error}
            </p>
          )}

          {!visorVideo.cargando && visorVideo.videos.length > 0 && (() => {
            const row = visorVideo.videos[visorVideo.indice];
            const resolved = resolveVideoForPresentacion(row);
            return (
              <>
                {visorVideo.videos.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      visorVideoAnterior();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10"
                  >
                    ‹
                  </button>
                )}

                {resolved?.kind === "youtube" && (
                  <iframe
                    key={visorVideo.indice}
                    title="Vídeo"
                    src={resolved.embedUrl}
                    className="w-[min(96vw,1200px)] max-h-[85vh] aspect-video border-0 rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {resolved?.kind === "file" && (
                  <video
                    key={resolved.src}
                    src={resolved.src}
                    controls
                    playsInline
                    autoPlay
                    className="max-w-[98vw] max-h-[85vh] rounded-lg"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {!resolved && (
                  <p className="text-white text-sm" onClick={(e) => e.stopPropagation()}>
                    No se pudo reproducir este vídeo.
                  </p>
                )}

                {visorVideo.videos.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      visorVideoSiguiente();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10"
                  >
                    ›
                  </button>
                )}

                <div className="absolute bottom-4 text-white text-sm">
                  Vídeo {visorVideo.indice + 1} / {visorVideo.videos.length}
                  {visorVideo.referencia ? ` · Ref. ${visorVideo.referencia}` : ""}
                  <span className="opacity-70 ml-2 hidden sm:inline">← →</span>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}